import type { AxiosInstance } from "axios";
import { u2000SkipBusyIndicatorConfig } from "../u2000Core/axios/U2000AxiosRequestConfig";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { isValidId } from "../u2000Core/tools/U2000IdUtils";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";
import { formatCurrency, type CurrencyFormatOptions } from "../u2000Core/tools/U2000CurrencyUtils";
import {
  type U3134ExpenseTypeRef,
  type U3134TaskRef,
  type U3134WorkInProgressSearchRow,
  type U3134TransactionKind,
} from "../u3134WorkInProgress/U3134Service";
import {
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
} from "../u2000Core/reducer/formReducer/U2000FormState";
import { U2000_AGING_BUCKETS, type U2000AgingBucket } from "../u2000Core/constants/U2000AgingBuckets";
import { U2000ApiValueToDisplayYmd, u2000TodayOrgYmd } from "../u2000Core/tools/U2000DateTimeHelpers";

const LOGICALUNIT_URL = "U3125";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  PDF: (id: string | number) => string;
  CUSTOMER_CREDIT_BALANCE: (customerId: string) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  /** `POST` — corps `U3125InvoiceSetStatusSchema` ; param URL = `body.id`. */
  SET_STATUS: (id: string | number) => string;
  /** `GET` — TEC non facturées par projet. */
  WIP_TRANSACTIONS: string;
  INIT_CREATE_NEW_INVOICE: string;
  FORFAIT_WIZARD_PREVIEW: string;
  FORFAIT_WIZARD_COMMIT: string;
  PAYMENT_CREATE: string;
  PAYMENT_UPDATE: (id: string | number) => string;
  PAYMENT_DELETE: (id: string | number) => string;
  /** `POST` — corps `U3125InvoiceVoidSchema` ; param URL = `body.id`. */
  VOID: (id: string | number) => string;
  /** `POST` — rapport âge des comptes à recevoir complet (BL-0028, fallback UI). */
  AR_AGING_REPORT: string;
  /** `POST` — sommaire CAR sans projects/invoices (BL-0028 lazy-load). */
  AR_AGING_SUMMARY: string;
  /** `POST` — détail client CAR (BL-0028 lazy-load). */
  AR_AGING_CUSTOMER_DETAIL: string;
  AR_AGING_EXPORT_EXCEL: string;
  AR_AGING_EXPORT_PDF: string;
  EXPORT_EXCEL: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  PDF: (id: string | number) => `${LOGICALUNIT_URL}/${id}/pdf`,
  CUSTOMER_CREDIT_BALANCE: (customerId: string) => `${LOGICALUNIT_URL}/customerCreditBalance/${customerId}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  SET_STATUS: (id: string | number) => `${LOGICALUNIT_URL}/status/${id}`,
  WIP_TRANSACTIONS: `${LOGICALUNIT_URL}/wipTransactions`,
  INIT_CREATE_NEW_INVOICE: `${LOGICALUNIT_URL}/initCreateNewInvoice`,
  FORFAIT_WIZARD_PREVIEW: `${LOGICALUNIT_URL}/forfait-invoice-wizard/preview`,
  FORFAIT_WIZARD_COMMIT: `${LOGICALUNIT_URL}/forfait-invoice-wizard/commit`,
  PAYMENT_CREATE: `${LOGICALUNIT_URL}/payments/create`,
  PAYMENT_UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/payments/update/${id}`,
  PAYMENT_DELETE: (id: string | number) => `${LOGICALUNIT_URL}/payments/delete/${id}`,
  VOID: (id: string | number) => `${LOGICALUNIT_URL}/void/${id}`,
  AR_AGING_REPORT: `${LOGICALUNIT_URL}/reports/accountsReceivableAging`,
  AR_AGING_SUMMARY: `${LOGICALUNIT_URL}/reports/accountsReceivableAging/summary`,
  AR_AGING_CUSTOMER_DETAIL: `${LOGICALUNIT_URL}/reports/accountsReceivableAging/customerDetail`,
  AR_AGING_EXPORT_EXCEL: `${LOGICALUNIT_URL}/reports/accountsReceivableAging/exportExcel`,
  AR_AGING_EXPORT_PDF: `${LOGICALUNIT_URL}/reports/accountsReceivableAging/exportPdf`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
};

/** Résumé projet (GET facture / initCreate / lignes recherche). */
export interface U3125InvoiceProjectSummary {
  id?: string;
  projetNumber?: string;
  projetName?: string;
  fixedPrice?: boolean;
  fixedAmount?: number;
  hourlyMarkup?: number;
  sellingRate?: number;
  billingFrequency?: string | null;
  subProjects?: U3125InitCreateSubProject[];
  /** Client du projet (réponse search BL-0088). */
  customer?: Pick<U3125InitCreateCustomer, "id" | "customerCode" | "customerName"> | null;
}

export type U3125InvoiceTypeNature = "STANDARD" | "CREDIT" | "ADVANCE" | "WIP_WRITE_OFF";

export function u3125IsWriteOffDocument(documentNature: U3125InitCreateDocumentNature): boolean {
  return documentNature === "WIP_WRITE_OFF";
}

/**
 * BL-0271 — onglet Lignes de facture : opt-in perso + hors radiation TEC.
 */
export function u3125ShowInvoiceLinesTab(enableInvoicePdfAndLines: boolean, isWriteOff: boolean): boolean {
  return Boolean(enableInvoicePdfAndLines) && !isWriteOff;
}

/**
 * BL-0271 — bouton PDF facture : opt-in perso + hors radiation + statut RELEASE/CLOSE.
 */
export function u3125CanOpenInvoicePdf(
  enableInvoicePdfAndLines: boolean,
  isWriteOff: boolean,
  status: string | null | undefined,
): boolean {
  return Boolean(enableInvoicePdfAndLines) && !isWriteOff && (status === "RELEASE" || status === "CLOSE");
}

/**
 * Codes facturation standard forfait/horaire (partielle / finale).
 * Pas de colonne `is_final_invoice` en base — la distinction est portée par `pr_invoice_type.code`.
 */
export const U3125_STANDARD_BILLING_INVOICE_TYPE_CODES = ["FP", "FF"] as const;

export type U3125StandardBillingInvoiceTypeCode = (typeof U3125_STANDARD_BILLING_INVOICE_TYPE_CODES)[number];

export function u3125IsStandardBillingInvoiceTypeCode(code: string | null | undefined): code is U3125StandardBillingInvoiceTypeCode {
  const normalized = code?.trim().toUpperCase();
  return normalized === "FP" || normalized === "FF";
}

/** Types actifs FP / FF (nouvelle facture standard). */
export function u3125FilterStandardBillingInvoiceTypes(types: U3125InvoiceTypeInitItem[]): U3125InvoiceTypeInitItem[] {
  return types.filter((t) => t.active && u3125IsStandardBillingInvoiceTypeCode(t.code));
}

/** Types actifs pour une nature document (standard / crédit / acompte). */
export function u3125FilterInvoiceTypesByDocumentNature(
  types: U3125InvoiceTypeInitItem[],
  documentNature: U3125InitCreateDocumentNature,
): U3125InvoiceTypeInitItem[] {
  return types.filter((t) => t.active && t.nature === documentNature);
}

export type U3125InvoiceStatus = "OPEN" | "ON_HOLD" | "RELEASE" | "CLOSE" | "VOID";

export interface U3125InvoiceStatusInitItem {
  code: U3125InvoiceStatus;
  label: string;
}

/** Nature de document (initLU — ComboBox critères liste BL-0432). */
export interface U3125DocumentNatureInitItem {
  code: U3125InvoiceTypeNature;
  label: string;
}

/** Composante TPS/TVQ (initLU, aligné U3129 / U3100). */
export interface U3125TaxRateComponentInitItem {
  id: string;
  sortOrder: number;
  rate: number;
  labelFr?: string | null;
  labelEn?: string | null;
  applicationMode: string | null;
}

/** Régime de taxes ts_tax_rate (initLU). */
export interface U3125TaxRateInitItem {
  id: string;
  code?: string | null;
  labelFr?: string | null;
  labelEn?: string | null;
  /** Si `false`, régime inactif — ne pas proposer en UI. */
  active?: boolean;
  countryId?: string | null;
  provinceId?: string | null;
  taxRateComponents: U3125TaxRateComponentInitItem[];
}

/** Taux sélectionnable pour la facture : actif uniquement (`active === true`, comme les types de facture). */
export function u3125TaxRateIsActive(rate: U3125TaxRateInitItem): boolean {
  return rate.active === true;
}

export function u3125TaxRateMatchesInvoiceJurisdiction(
  rate: U3125TaxRateInitItem,
  invoiceCountryId: string | null | undefined,
  invoiceProvinceId: string | null | undefined,
): boolean {
  const rc = rate.countryId?.trim() || null;
  const rp = rate.provinceId?.trim() || null;
  if (!rc && !rp) return true;
  const ic = invoiceCountryId?.trim() || null;
  const ip = invoiceProvinceId?.trim() || null;
  if (!ic) return true;
  if (rc !== ic) return false;
  if (!rp) return true;
  if (!ip) return false;
  return rp === ip;
}

export function u3125FilterTaxRatesForInvoiceSelection(
  rates: U3125TaxRateInitItem[],
  opts: {
    customerTaxable?: boolean;
    invoiceCountryId?: string | null;
    invoiceProvinceId?: string | null;
  },
): U3125TaxRateInitItem[] {
  if (opts.customerTaxable === false) return [];
  let out = rates.filter(u3125TaxRateIsActive);
  const ic = opts.invoiceCountryId?.trim() || null;
  const ip = opts.invoiceProvinceId?.trim() || null;
  if (ic) {
    out = out.filter((r) => u3125TaxRateMatchesInvoiceJurisdiction(r, ic, ip));
  }
  return out;
}

export interface U3125InvoiceTypeTranslation {
  languageCode: string;
  invoiceTypeLabel: string;
}

export interface U3125InvoiceTypeInitItem {
  id: string;
  code: string;
  active: boolean;
  nature: U3125InvoiceTypeNature;
  translations: U3125InvoiceTypeTranslation[];
}

export interface U3125PaymentTypeTranslation {
  languageCode: string;
  paymentTypeLabel: string;
}

/** Type de paiement pr_invoice_payment_type (initLU). */
export interface U3125PaymentTypeInitItem {
  id: string;
  code: string;
  active: boolean;
  translations: U3125PaymentTypeTranslation[];
}

/**
 * Codes type de paiement par nature document (`pr_invoice_payment_type`).
 * STANDARD : encaissement final / partiel ; CREDIT : escompte ; ADVANCE : acompte partiel.
 */
export const U3125_PAYMENT_TYPE_CODES_BY_DOCUMENT_NATURE = {
  STANDARD: ["EF", "EP"],
  CREDIT: ["ES"],
  ADVANCE: ["AP"],
  WIP_WRITE_OFF: [],
} as const satisfies Record<U3125InvoiceTypeNature, readonly string[]>;

export function u3125FilterPaymentTypesByDocumentNature(
  types: U3125PaymentTypeInitItem[],
  documentNature: U3125InvoiceTypeNature,
): U3125PaymentTypeInitItem[] {
  const allowed = new Set(U3125_PAYMENT_TYPE_CODES_BY_DOCUMENT_NATURE[documentNature].map((c) => c.toUpperCase()));
  return types.filter((t) => t.active && allowed.has(t.code.trim().toUpperCase()));
}

/** Types de paiement sélectionnables : filtrés par nature + code courant si hors liste (inactif / legacy). */
export function u3125SelectablePaymentTypes(
  types: U3125PaymentTypeInitItem[],
  documentNature: U3125InvoiceTypeNature,
  currentCode?: string | null,
): U3125PaymentTypeInitItem[] {
  const filtered = u3125FilterPaymentTypesByDocumentNature(types, documentNature);
  const codes = new Set(filtered.map((t) => t.code.trim().toUpperCase()));
  const current = currentCode?.trim().toUpperCase();
  if (current && !codes.has(current)) {
    const row = types.find((t) => t.code.trim().toUpperCase() === current);
    if (row) return [...filtered, row];
  }
  return filtered;
}

/**
 * Type de paiement suggéré selon nature document, montant reçu et solde restant.
 * STANDARD : EF si paiement intégral, EP si partiel ; CREDIT → ES ; ADVANCE → AP.
 */
export function u3125SuggestPaymentTypeCode(
  documentNature: U3125InvoiceTypeNature,
  receivedAmount: number,
  remainingGrandTotal: number,
): string | null {
  if (documentNature === "CREDIT") return "ES";
  if (documentNature === "ADVANCE") return "AP";
  const received = u3125RoundTaxAmount(receivedAmount);
  const remaining = u3125RoundTaxAmount(remainingGrandTotal);
  if (received <= 0 || remaining <= 0) return null;
  if (received >= remaining - 0.005) return "EF";
  return "EP";
}

/** Liste combo paiement : nature document + montant reçu / solde (STANDARD → EF ou EP seulement). */
export function u3125SelectablePaymentTypesForPayment(
  types: U3125PaymentTypeInitItem[],
  documentNature: U3125InvoiceTypeNature,
  opts: {
    currentCode?: string | null;
    receivedAmount?: number;
    remainingGrandTotal?: number;
  },
): U3125PaymentTypeInitItem[] {
  const base = u3125SelectablePaymentTypes(types, documentNature, opts.currentCode);
  if (documentNature !== "STANDARD") return base;

  const suggested = u3125SuggestPaymentTypeCode(
    documentNature,
    opts.receivedAmount ?? 0,
    opts.remainingGrandTotal ?? 0,
  );
  if (!suggested) return base;

  const allowed = new Set([suggested.toUpperCase()]);
  const current = opts.currentCode?.trim().toUpperCase();
  if (current) allowed.add(current);

  return base.filter((t) => allowed.has(t.code.trim().toUpperCase()));
}

/** Pays (GET facture, optionnel). */
export interface U3125InvoiceCountryRef {
  id: string;
  countryNameFr?: string | null;
  countryNameEn?: string | null;
  countryName?: string | null;
}

/** Province (GET facture, optionnel). */
export interface U3125InvoiceProvinceRef {
  id: string;
  stateNameFr?: string | null;
  stateNameEn?: string | null;
  stateName?: string | null;
}

/** Pays / province sur le formulaire facture (autocomplétion). */
export type U3125InvoiceFormCountry = {
  id: string | null;
  countryNameFr?: string | null;
  countryNameEn?: string | null;
  countryName?: string | null;
  name?: string | null;
};

export type U3125InvoiceFormProvince = {
  id: string | null;
  stateNameFr?: string | null;
  stateNameEn?: string | null;
  stateName?: string | null;
  name?: string | null;
};

export function emptyU3125InvoiceFormCountry(): U3125InvoiceFormCountry {
  return { id: null, countryNameFr: "", countryNameEn: "", countryName: "", name: "" };
}

export function emptyU3125InvoiceFormProvince(): U3125InvoiceFormProvince {
  return { id: null, stateNameFr: "", stateNameEn: "", stateName: "", name: "" };
}

/** Ligne de paiement (réponse GET ou corps save). */
export interface U3125InvoicePayment {
  id?: string;
  version?: number;
  paymentDate: string;
  paymentTypeCode: string;
  amountTotal: number;
  amountTaxable: number;
  amountNonTaxable: number;
  taxAmount1?: number | null;
  taxAmount2?: number | null;
  remark?: string | null;
}

export const U3125_INVOICE_LINE_KINDS = [
  "LABOR",
  "EXPENSE",
  "EQUIPMENT",
  "FREE",
  "DISCOUNT",
  "WRITE_OFF",
] as const;
export type U3125InvoiceLineKind = (typeof U3125_INVOICE_LINE_KINDS)[number];

/** Ligne de facture (GET / formulaire). */
export interface U3125InvoiceLineItem {
  id?: string;
  version?: number;
  position: number;
  kind: U3125InvoiceLineKind;
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  amount: number;
  taxable: boolean;
  wipAmount?: number | null;
  wipLineIds: string[];
  appliedFromInvoiceId?: string | null;
}

/** Ligne affichée en grille (id stable pour la sélection / édition). */
export type U3125InvoiceLineGridRow = U3125InvoiceLineItem & { id: string };

/** Paiement affiché en grille. */
export type U3125InvoicePaymentGridRow = U3125InvoicePayment & { id: string };

/** Facture (réponse API). */
/** Détail du régime de taxes associé à la facture (renvoyé par GET, incluant taux inactifs). */
export interface U3125InvoiceTaxesRef {
  code: string;
  labelFr?: string | null;
  labelEn?: string | null;
}

export interface U3125Invoice {
  id: string;
  version: number;
  /** Null tant que RELEASE sans numéro auto. Chaîne numérique (`"42"`) ou radiation TEC (`"W-1"`). */
  invoiceNumber: string | null;
  invoiceDate: string;
  status: U3125InvoiceStatus;
  invoiceTypeCode: string;
  taxRateCode: string;
  /** Détail (code + libellés) du régime de taxes pour affichage en lecture. */
  taxes?: U3125InvoiceTaxesRef | null;
  /** Client taxable (défaut métier true si absent). */
  customerTaxable?: boolean;
  countryId?: string | null;
  provinceId?: string | null;
  country?: U3125InvoiceCountryRef | null;
  province?: U3125InvoiceProvinceRef | null;
  amountTec: number;
  amountInvoiceTaxable: number;
  amountInvoiceNonTaxable: number;
  taxAmount1?: number | null;
  taxAmount2?: number | null;
  totalsOverride: boolean;
  originalInvoiceId?: string | null;
  remark?: string | null;
  /** Nature document persistée (`pr_invoice.document_nature`). */
  documentNature: U3125InitCreateDocumentNature;
  project: U3125InvoiceProjectSummary;
  /** Client du projet (même forme que `initCreateNewInvoice.customer`). */
  customer?: U3125InitCreateCustomer;
  payments: U3125InvoicePayment[];
  lines: U3125InvoiceLineItem[];
  warnings?: string[];
  workInProgressLineIds?: string[];
  /**
   * TEC non facturées + TEC liées à la facture (comme `initCreateNewInvoice` / `wipTransactions`),
   * avec `selected` si l’ID est dans `workInProgressLineIds`.
   */
  workInProgressLines?: U3125InitCreateWipLine[];
  /**
   * Statistiques projet (`loadProjectInvoicingStats`) — sémantique selon `project.fixedPrice` :
   * - forfait : montant déjà facturé (RELEASE/CLOSE) ; UI affiche `fixedAmount − invoiceAmount`
   * - horaire : total vendant des TEC non facturées (`sumUnbilledWipSellingAmount`)
   */
  invoiceAmount?: number;
  /** Nombre de factures RELEASE/CLOSE déjà émises sur le projet. */
  invoiceCount?: number;
  /** @deprecated Alias API — même sémantique que `invoiceAmount`. */
  invoicedAmount?: number;
  /** @deprecated Alias API — même sémantique que `invoiceCount`. */
  invoicedCount?: number;
  /** Applications crédit / acompte (facture standard uniquement). */
  creditApplications?: U3125InvoiceCreditApplicationItem[];
  /** Totaux après application crédits / acomptes (facture standard uniquement). */
  billingTotals?: U3125InvoiceBillingTotals | null;
  /** Consommation du pool (document CREDIT / ADVANCE source uniquement). */
  sourceUsageSummary?: U3125SourceUsageSummary | null;
  /** Alerte soldes NC / acomptes (facture STANDARD OPEN uniquement — BL-0061). */
  availableCreditAlert?: U3125AvailableCreditAlert | null;
}

/** Facture standard cible d'une application crédit / acompte. */
export interface U3125SourceUsageConsumption {
  targetInvoiceId: string;
  targetInvoiceNumber: string | null;
  targetInvoiceDate: string;
  targetInvoiceStatus: U3125InvoiceStatus;
  amount: number;
}

/**
 * Résumé d'utilisation d'un document CREDIT ou ADVANCE source.
 * - CREDIT : `originalAmount` / `remainingAmount` en **avant taxes** (imposable + non imposable, signé côté API).
 * - ADVANCE : montants en **TTC** (grand total document).
 */
export interface U3125SourceUsageSummary {
  originalAmount: number;
  appliedAmount: number;
  remainingAmount: number;
  fullyConsumed: boolean;
  consumptions: U3125SourceUsageConsumption[];
}

export type U3125SourceUsageStatus = "UNUSED" | "PARTIAL" | "FULL";

export type U3125SourceUsageConsumptionGridRow = U3125SourceUsageConsumption & { id: string };

export type U3125InitCreateDocumentNature = "STANDARD" | "CREDIT" | "ADVANCE" | "WIP_WRITE_OFF";

export interface U3125OriginalInvoiceCandidate {
  id: string;
  invoiceNumber: string | null;
  description: string;
  invoiceTypeCode: string;
  invoiceTypeLabel: string;
  amountInvoiceNonTaxable: number;
}

export interface U3125InitCreateSubProject {
  id?: string;
  subProjetNumber?: string;
  subProjetName?: string;
}

export interface U3125InitCreateProject {
  id?: string;
  projetNumber?: string;
  projetName?: string;
  fixedPrice?: boolean;
  fixedAmount?: number;
  hourlyMarkup?: number;
  sellingRate?: number;
  billingFrequency?: string | null;
  subProjects?: U3125InitCreateSubProject[];
}

export interface U3125InitCreateCustomer {
  id?: string;
  customerCode?: string;
  customerName?: string;
  taxable?: boolean;
}

export type U3125InitCreateWipLine = U3134WorkInProgressSearchRow & { selected: boolean };

export interface U3125InitCreateNewInvoice {
  documentNature: U3125InitCreateDocumentNature;
  status: U3125InvoiceStatus;
  invoiceTypeCode: string;
  invoiceDate: string;
  project: U3125InitCreateProject;
  customer: U3125InitCreateCustomer;
  amountTec: number;
  amountInvoiceTaxable: number;
  amountInvoiceNonTaxable: number;
  taxRateCode?: string;
  taxAmount1?: number | null;
  taxAmount2?: number | null;
  country?: { id: string } | null;
  province?: { id: string } | null;
  workInProgressLines: U3125InitCreateWipLine[];
  workInProgressLineIds: string[];
  originalInvoiceCandidates?: U3125OriginalInvoiceCandidate[];
  /**
   * Statistiques projet — voir `U3125Invoice.invoiceAmount` (forfait vs horaire).
   */
  invoiceAmount?: number;
  /** Nombre de factures RELEASE/CLOSE déjà émises sur le projet. */
  invoiceCount?: number;
  /** @deprecated Alias API — même sémantique que `invoiceAmount`. */
  invoicedAmount?: number;
  /** @deprecated Alias API — même sémantique que `invoiceCount`. */
  invoicedCount?: number;
  /** Alerte soldes NC / acomptes (preview STANDARD OPEN — BL-0061). */
  availableCreditAlert?: U3125AvailableCreditAlert | null;
}

export interface U3125InitCreateNewInvoiceRequest {
  projetNumber: string;
  documentNature: U3125InitCreateDocumentNature;
}

export interface U3125InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  admIncludeSubProject?: string | null;
  /**
   * BL-0271 — perso `adm_u3125_enable_invoice_pdf_lines` (opt-in).
   * Défaut `false` : masquer bouton PDF + onglet Lignes. Si `true` : PDF + onglet selon règles métier.
   */
  enableInvoicePdfAndLines?: boolean;
  /**
   * BL-0654 — perso `adm_u3125_allow_invoice_above_budget` (admin, opt-in).
   * Défaut `false` : plafond budget sur montants facture STANDARD + wizard forfait.
   */
  allowInvoiceAboveBudget?: boolean;
  invoiceTypes: U3125InvoiceTypeInitItem[];
  /** Statuts cycle de vie (libellés FR depuis l’API). */
  invoiceStatuses: U3125InvoiceStatusInitItem[];
  /** Natures de document (libellés FR depuis l’API — BL-0432). */
  documentNatures: U3125DocumentNatureInitItem[];
  /** Régimes de taxes organisation (tri par code). */
  taxRates: U3125TaxRateInitItem[];
  /** Types de paiement organisation (tri par code). */
  paymentTypes: U3125PaymentTypeInitItem[];
  /** Fuseau d'affichage organisation (IANA). Défaut frontend : America/Montreal. */
  organizationTimezone?: string;
}

/**
 * Source de crédit / acompte (`GET /U3125/customerCreditBalance/:customerId`).
 * Filtrage **backend** (`u3125IsCreditApplicationSourceStatusEligible`) — pas de filtre statut côté UI.
 * - **CREDIT** : statut source **RELEASE** uniquement ; montants **avant taxes** ; pas de condition de paiement.
 * - **ADVANCE** : statut **RELEASE** ou **CLOSE** ; montants **TTC** ; document **payé intégralement** (`Σ paiements ≥ total TTC`).
 */
export interface U3125CreditSource {
  invoiceId: string;
  invoiceNumber: string | null;
  invoiceDate: string;
  invoiceTypeCode: string;
  nature: U3125InvoiceTypeNature;
  originalAmount: number;
  appliedAmount: number;
  remainingAmount: number;
  /** CREDIT uniquement — composantes HT ventilées. */
  originalTaxable?: number;
  originalNonTaxable?: number;
  appliedTaxable?: number;
  appliedNonTaxable?: number;
  remainingTaxable?: number;
  remainingNonTaxable?: number;
  projectId: string;
  projetNumber: string | null;
  projetName: string | null;
}

export interface U3125CustomerCreditBalance {
  customerId: string;
  totalBalance: number;
  availableSources: U3125CreditSource[];
}

/** Application crédit / acompte sur facture standard (GET / create / update). */
export interface U3125InvoiceCreditApplicationItem {
  id?: string;
  version?: number;
  position: number;
  sourceInvoiceId: string;
  sourceNature?: U3125InvoiceTypeNature;
  sourceInvoiceNumber?: string | null;
  sourceInvoiceDate?: string | null;
  sourceInvoiceTypeCode?: string;
  amount: number;
  amountTaxable?: number;
  amountNonTaxable?: number;
}

export type U3125InvoiceCreditApplicationGridRow = U3125InvoiceCreditApplicationItem & { id: string };

/** Totaux facture standard incluant crédits et acomptes (GET / create / update). */
export interface U3125InvoiceBillingTotals {
  creditApplicationsBeforeTaxTotal: number;
  advanceApplicationsTotal: number;
  invoiceTotal: number;
  grandTotal: number;
}

/** Agrégats soldes disponibles — notes de crédit (HT) et acomptes (TTC). */
export interface U3125AvailableCreditAlertLevel {
  creditRemaining: number;
  advanceRemaining: number;
}

/** Alerte facture OPEN STANDARD : soldes NC / acomptes client et projet (BL-0061). */
export interface U3125AvailableCreditAlert {
  showAlert: boolean;
  customer: U3125AvailableCreditAlertLevel;
  project?: U3125AvailableCreditAlertLevel;
}

/** Seuil d’affichage aligné backend (`customerCreditBalance` / `showAlert`). */
export const U3125_AVAILABLE_CREDIT_ALERT_AMOUNT_THRESHOLD = 0.005;

const U3125_ALERT_CURRENCY_FORMAT: CurrencyFormatOptions = {
  currencySymbol: "$",
  symbolPosition: "left",
  decimalSeparator: ".",
  thousandSeparator: " ",
  decimalDigits: 2,
  allowNegative: true,
};

export function u3125FormatAlertCurrency(value: number): string {
  return formatCurrency(value, U3125_ALERT_CURRENCY_FORMAT);
}

export function mapAvailableCreditAlertFromApi(raw: unknown): U3125AvailableCreditAlert | null {
  if (!raw || typeof raw !== "object") return null;
  const alert = raw as U3125AvailableCreditAlert;
  if (!alert.customer || typeof alert.customer !== "object") return null;
  return {
    showAlert: Boolean(alert.showAlert),
    customer: {
      creditRemaining: Number(alert.customer.creditRemaining) || 0,
      advanceRemaining: Number(alert.customer.advanceRemaining) || 0,
    },
    project: alert.project
      ? {
          creditRemaining: Number(alert.project.creditRemaining) || 0,
          advanceRemaining: Number(alert.project.advanceRemaining) || 0,
        }
      : undefined,
  };
}

export function u3125ShouldShowAvailableCreditAlert(
  row: Pick<U3125InvoiceFormRow, "documentNature" | "status" | "availableCreditAlert"> | null | undefined,
): boolean {
  if (!row) return false;
  return row.documentNature === "STANDARD" && row.status === "OPEN" && row.availableCreditAlert?.showAlert === true;
}

export type U3125AvailableCreditAlertDisplayLine = { text: string };

type U3125AvailableCreditAlertNature = "credit" | "advance";

function u3125PushAvailableCreditAlertNatureLine(
  lines: U3125AvailableCreditAlertDisplayLine[],
  t: TranslateFn,
  nature: U3125AvailableCreditAlertNature,
  customerAmount: number,
  projectAmount: number,
): void {
  const hasCustomer = customerAmount > U3125_AVAILABLE_CREDIT_ALERT_AMOUNT_THRESHOLD;
  const hasProject = projectAmount > U3125_AVAILABLE_CREDIT_ALERT_AMOUNT_THRESHOLD;
  if (!hasCustomer && !hasProject) return;

  const prefix = nature === "credit" ? "Credit" : "Advance";
  const customerFmt = u3125FormatAlertCurrency(customerAmount);
  const projectFmt = u3125FormatAlertCurrency(projectAmount);

  if (hasCustomer && hasProject) {
    lines.push({
      text: t(`msgAvailableCreditAlert${prefix}Line`, {
        ns: "u3125",
        customerAmount: customerFmt,
        projectAmount: projectFmt,
      }),
    });
    return;
  }
  if (hasCustomer) {
    lines.push({
      text: t(`msgAvailableCreditAlert${prefix}LineCustomerOnly`, { ns: "u3125", amount: customerFmt }),
    });
    return;
  }
  lines.push({
    text: t(`msgAvailableCreditAlert${prefix}LineProjectOnly`, { ns: "u3125", amount: projectFmt }),
  });
}

/** Au plus une ligne notes de crédit et une ligne acomptes — même logique (client) / (ce projet). */
export function u3125AvailableCreditAlertDisplayLines(
  alert: U3125AvailableCreditAlert,
  t: TranslateFn,
): U3125AvailableCreditAlertDisplayLine[] {
  const lines: U3125AvailableCreditAlertDisplayLine[] = [];
  u3125PushAvailableCreditAlertNatureLine(
    lines,
    t,
    "credit",
    Number(alert.customer.creditRemaining) || 0,
    Number(alert.project?.creditRemaining) || 0,
  );
  u3125PushAvailableCreditAlertNatureLine(
    lines,
    t,
    "advance",
    Number(alert.customer.advanceRemaining) || 0,
    Number(alert.project?.advanceRemaining) || 0,
  );
  return lines;
}

/** Ligne résultat recherche. */
export interface U3125InvoiceSearchRow {
  id: string;
  version?: number;
  invoiceNumber: string | null;
  invoiceDate: string;
  status: U3125InvoiceStatus;
  /** Nature de document (search result — BL-0432). */
  documentNature?: U3125InvoiceTypeNature | null;
  invoiceTypeCode: string;
  taxRateCode: string;
  amountTec: number;
  amountInvoiceTaxable: number;
  amountInvoiceNonTaxable: number;
  taxAmount1?: number | null;
  taxAmount2?: number | null;
  totalsOverride: boolean;
  remark?: string | null;
  project: U3125InvoiceProjectSummary;
}

/** Modèle formulaire (état écran). */
export interface U3125InvoiceFormRow {
  id: string;
  version: number;
  useAutoInvoiceNumber: boolean;
  invoiceNumber: string | null;
  invoiceDate: string;
  status: U3125InvoiceStatus;
  /** Nature document (lecture seule — choix dialog Ajouter / colonne persistée). */
  documentNature: U3125InitCreateDocumentNature;
  invoiceTypeCode: string;
  taxRateCode: string;
  /** Détail du régime de taxes (renvoyé par GET) pour affichage en lecture seule. */
  taxes: U3125InvoiceTaxesRef | null;
  customerTaxable: boolean;
  country: U3125InvoiceFormCountry;
  province: U3125InvoiceFormProvince;
  amountTec: number;
  amountInvoiceTaxable: number;
  amountInvoiceNonTaxable: number;
  taxAmount1: number | null;
  taxAmount2: number | null;
  totalsOverride: boolean;
  originalInvoiceId: string;
  /** Candidats facture d’origine (preview init create — crédit / acompte). */
  originalInvoiceCandidates?: U3125OriginalInvoiceCandidate[];
  remark: string;
  project: U3125InvoiceProjectSummary | null;
  workInProgressLineIds: string[];
  /** TEC du projet (non facturées + liées) — stat « TEC restant à facturer ». */
  workInProgressLines?: U3134WorkInProgressSearchRow[];
  lines: U3125InvoiceLineItem[];
  payments: U3125InvoicePayment[];
  /**
   * Statistiques projet — voir `U3125Invoice.invoiceAmount` (forfait vs horaire).
   */
  invoiceAmount?: number;
  /** Nombre de factures RELEASE/CLOSE déjà émises sur le projet. */
  invoiceCount?: number;
  /** Client du projet (init create / GET). */
  customer?: U3125InitCreateCustomer;
  /** Sources crédit / acompte disponibles (chargées via customerCreditBalance — non persistées). */
  availableCreditSources?: U3125CreditSource[];
  /** Applications crédit / acompte sur la facture courante. */
  creditApplications: U3125InvoiceCreditApplicationItem[];
  /** Totaux après crédits / acomptes (renvoyés par GET / save). */
  billingTotals: U3125InvoiceBillingTotals | null;
  /** Utilisation du document source (CREDIT / ADVANCE — renvoyé par GET / save). */
  sourceUsageSummary: U3125SourceUsageSummary | null;
  /** Alerte soldes NC / acomptes (GET / initCreate — non persisté). */
  availableCreditAlert: U3125AvailableCreditAlert | null;
}

export function pickInvoiceTypeLabel(
  types: U3125InvoiceTypeInitItem[],
  code: string,
  languageCode: string,
): string {
  const item = types.find((t) => t.code === code);
  if (!item) return code;
  const lang = (languageCode || "fr").slice(0, 2).toLowerCase();
  const tr = item.translations.find((x) => x.languageCode.toLowerCase() === lang);
  const trFallback = item.translations[0];
  return tr?.invoiceTypeLabel ?? trFallback?.invoiceTypeLabel ?? item.code;
}

export function pickPaymentTypeLabel(types: U3125PaymentTypeInitItem[], code: string, languageCode: string): string {
  const item = types.find((t) => t.code === code);
  if (!item) return code;
  const lang = (languageCode || "fr").slice(0, 2).toLowerCase();
  const tr = item.translations.find((x) => x.languageCode.toLowerCase().slice(0, 2) === lang);
  const trFallback = item.translations[0];
  return tr?.paymentTypeLabel ?? trFallback?.paymentTypeLabel ?? item.code;
}

export function pickInvoiceStatusLabel(t: TranslateFn, code: U3125InvoiceStatus | string): string {
  const c = String(code ?? "").trim();
  if (!c) return "";
  return t(`invoiceStatus_${c}`, { ns: "u3125", defaultValue: c });
}

export function u3125FormatBillingFrequency(freq: string | null | undefined, t: TranslateFn, ns: string): string {
  const f = freq?.trim();
  if (!f) return "";
  const upper = f.toUpperCase();
  if (upper === "ENDPROJECT") return t("billingFrequencyEndProject", { ns });
  if (upper === "BYSTEP") return t("billingFrequencyByStep", { ns });
  return f;
}

export function u3125FormatProjectBillingType(value: boolean | undefined, t: TranslateFn, ns: string): string {
  if (value === undefined) return "";
  return value ? t("lblFixedPrice", { ns }) : t("lblHourly", { ns });
}

export function u3125FormatProjectHourlyMarkup(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "";
  return String(value);
}

/** Suppression autorisée si statut `OPEN` ou `ON_HOLD`. */
export function u3125CanDeleteInvoice(status: U3125InvoiceStatus | undefined | null): boolean {
  return status === "OPEN" || status === "ON_HOLD";
}

/** Modification autorisée uniquement si statut `OPEN`. */
export function u3125CanModifyInvoice(status: U3125InvoiceStatus | undefined | null): boolean {
  return status === "OPEN";
}

/** Case « officialiser à l'enregistrement » — create ou edit OPEN (pas RELEASE/CLOSE). */
export function u3125ShowReleaseAfterCreateCheckbox(
  editMode: U2000FormStateEnumEditMode,
  status: U3125InvoiceStatus | undefined | null,
  formState: U2000FormStateEnumState,
): boolean {
  if (formState === U2000FormStateEnumState.READ) {
    return false;
  }
  if (editMode === U2000FormStateEnumEditMode.ADD) {
    return true;
  }
  if (editMode === U2000FormStateEnumEditMode.EDIT && u3125CanModifyInvoice(status)) {
    return true;
  }
  return false;
}

/** Paiements (create/update/delete) autorisés uniquement si statut `RELEASE`. */
export function u3125CanManagePayments(status: U3125InvoiceStatus | undefined | null): boolean {
  return status === "RELEASE";
}

/** Tolérance alignée backend `u3125AssertAdvanceVoidEligible` (BL-0019). */
export const U3125_VOID_ELIGIBILITY_AMOUNT_EPS = 0.005;

/** Document source CREDIT/ADVANCE non consommé — miroir `U3125VoidInvoiceRules`. */
export function u3125IsSourceUnusedForVoid(usage: U3125SourceUsageSummary): boolean {
  if (usage.consumptions.length > 0) return false;
  if (usage.fullyConsumed) return false;
  if (Math.abs(Number(usage.appliedAmount ?? 0)) > U3125_VOID_ELIGIBILITY_AMOUNT_EPS) return false;
  return true;
}

/** Void autorisé : RELEASE, sans paiement client ; règles par nature (BL-0019). */
export function u3125CanVoidInvoice(
  row:
    | Pick<U3125InvoiceFormRow, "status" | "documentNature" | "payments" | "sourceUsageSummary">
    | null
    | undefined,
): boolean {
  if (!row) return false;
  if (row.status !== "RELEASE") return false;
  const clientPayments = (row.payments ?? []).filter((p) => !u3125IsCreditApplicationAutoPaymentRemark(p.remark));
  if (clientPayments.length > 0) return false;

  const nature = row.documentNature;
  if (nature === "STANDARD" || nature === "WIP_WRITE_OFF") return true;

  if (nature === "CREDIT" || nature === "ADVANCE") {
    const usage = row.sourceUsageSummary;
    if (!usage) return false;
    return u3125IsSourceUnusedForVoid(usage);
  }
  return false;
}

/** Montants composantes d'un paiement ou solde facture (hors `amountTotal`). */
export interface U3125PaymentComponentAmounts {
  amountTaxable: number;
  amountNonTaxable: number;
  taxAmount1: number;
  taxAmount2: number;
}

export type U3125AllocatedPaymentAmounts = U3125PaymentComponentAmounts & { amountTotal: number };

export function u3125PaymentComponentsTotal(components: U3125PaymentComponentAmounts): number {
  return u3125RoundTaxAmount(
    components.amountTaxable + components.amountNonTaxable + components.taxAmount1 + components.taxAmount2,
  );
}

export function u3125InvoiceToPaymentComponents(
  invoice: Pick<U3125InvoiceFormRow, "amountInvoiceTaxable" | "amountInvoiceNonTaxable" | "taxAmount1" | "taxAmount2">,
): U3125PaymentComponentAmounts {
  return {
    amountTaxable: Number(invoice.amountInvoiceTaxable ?? 0),
    amountNonTaxable: Number(invoice.amountInvoiceNonTaxable ?? 0),
    taxAmount1: Number(invoice.taxAmount1 ?? 0),
    taxAmount2: Number(invoice.taxAmount2 ?? 0),
  };
}

/** Somme des composantes déjà payées (option : exclure le paiement en cours d'édition). */
export function u3125SumPaymentComponents(
  payments: readonly U3125InvoicePayment[],
  excludePaymentId?: string,
): U3125PaymentComponentAmounts {
  const exclude = excludePaymentId?.trim();
  let amountTaxable = 0;
  let amountNonTaxable = 0;
  let taxAmount1 = 0;
  let taxAmount2 = 0;
  for (const payment of payments) {
    if (exclude && payment.id?.trim() === exclude) continue;
    amountTaxable += Number(payment.amountTaxable ?? 0);
    amountNonTaxable += Number(payment.amountNonTaxable ?? 0);
    taxAmount1 += Number(payment.taxAmount1 ?? 0);
    taxAmount2 += Number(payment.taxAmount2 ?? 0);
  }
  return {
    amountTaxable: u3125RoundTaxAmount(amountTaxable),
    amountNonTaxable: u3125RoundTaxAmount(amountNonTaxable),
    taxAmount1: u3125RoundTaxAmount(taxAmount1),
    taxAmount2: u3125RoundTaxAmount(taxAmount2),
  };
}

/** Totaux affichés en pied de grille onglet Paiements (somme de toutes les lignes). */
export type U3125PaymentGridTotals = U3125PaymentComponentAmounts & { amountTotal: number };

export function u3125SumPaymentGridTotals(payments: readonly U3125InvoicePayment[]): U3125PaymentGridTotals {
  let amountTotal = 0;
  for (const payment of payments) {
    amountTotal += Number(payment.amountTotal ?? 0);
  }
  return {
    amountTotal: u3125RoundTaxAmount(amountTotal),
    ...u3125SumPaymentComponents(payments),
  };
}

/** Solde restant par composante (facture − paiements antérieurs). */
export function u3125PaymentRemainingBalance(
  invoice: Pick<U3125InvoiceFormRow, "amountInvoiceTaxable" | "amountInvoiceNonTaxable" | "taxAmount1" | "taxAmount2">,
  payments: readonly U3125InvoicePayment[],
  excludePaymentId?: string,
): U3125PaymentComponentAmounts {
  const invoiceComponents = u3125InvoiceToPaymentComponents(invoice);
  const paid = u3125SumPaymentComponents(payments, excludePaymentId);
  return u3125SubtractPaymentComponents(invoiceComponents, paid);
}

/**
 * Pool net facture après consommation séquentielle des paiements (STANDARD).
 * Paiement auto acompte (BL-0021) : consommation **économique** en prorata sur le pool courant
 * (ligne en base reste 100 % non imposable). Paiements client : composantes enregistrées.
 */
export function u3125PaymentComponentsPoolAfterPayments(
  netBase: U3125PaymentComponentAmounts,
  payments: readonly U3125InvoicePayment[],
  customerTaxable: boolean | undefined,
  excludePaymentId?: string,
): U3125PaymentComponentAmounts {
  const exclude = excludePaymentId?.trim();
  const ordered = payments.filter((p) => !exclude || p.id?.trim() !== exclude);
  ordered.sort((a, b) => String(a.paymentDate ?? "").localeCompare(String(b.paymentDate ?? "")));

  let pool: U3125PaymentComponentAmounts = {
    amountTaxable: netBase.amountTaxable,
    amountNonTaxable: netBase.amountNonTaxable,
    taxAmount1: netBase.taxAmount1,
    taxAmount2: netBase.taxAmount2,
  };

  for (const payment of ordered) {
    if (u3125IsCreditApplicationAutoPaymentRemark(payment.remark)) {
      const amount = u3125RoundTaxAmount(Number(payment.amountTotal ?? 0));
      if (amount <= 0.005) continue;
      const slice = u3125AllocatePaymentFromReceivedAmount(amount, pool, customerTaxable);
      pool = u3125SubtractPaymentComponents(pool, slice);
      continue;
    }
    pool = u3125SubtractPaymentComponents(pool, {
      amountTaxable: Number(payment.amountTaxable ?? 0),
      amountNonTaxable: Number(payment.amountNonTaxable ?? 0),
      taxAmount1: Number(payment.taxAmount1 ?? 0),
      taxAmount2: Number(payment.taxAmount2 ?? 0),
    });
  }

  return pool;
}

/** Préfixe remark des paiements auto créés au RELEASE pour acomptes appliqués. */
export const U3125_CREDIT_APP_PAYMENT_REMARK_PREFIX = "CREDIT_APP:";

const U3125_CREDIT_APP_REMARK_SUFFIX_REGEX = /\|CREDIT_APP:[0-9a-f-]{36}\|$/i;

/** Libellé remark API des paiements auto acompte (BL-0021, sans préfixe technique). */
const U3125_ADVANCE_AUTO_REMARK_PREFIX = "Acompte appliqué —";

export function u3125IsCreditApplicationAutoPaymentRemark(remark: string | null | undefined): boolean {
  const trimmed = remark?.trim() ?? "";
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith(U3125_CREDIT_APP_PAYMENT_REMARK_PREFIX)) {
    return true;
  }
  if (U3125_CREDIT_APP_REMARK_SUFFIX_REGEX.test(trimmed)) {
    return true;
  }
  if (trimmed.startsWith(U3125_ADVANCE_AUTO_REMARK_PREFIX)) {
    return true;
  }
  return false;
}

/** Libellé remark pour l’UI (sans suffixe technique ni préfixe legacy CREDIT_APP:). */
export function u3125FormatAdvanceApplicationPaymentRemarkForDisplay(
  remark: string | null | undefined,
): string | null {
  if (remark == null) {
    return null;
  }
  let text = remark.trim();
  if (!text) {
    return null;
  }
  if (text.startsWith(U3125_CREDIT_APP_PAYMENT_REMARK_PREFIX)) {
    const legacySep = " — facture source n° ";
    const idx = text.indexOf(legacySep);
    if (idx >= 0) {
      const sourceNum = text.slice(idx + legacySep.length).trim();
      return `${U3125_ADVANCE_AUTO_REMARK_PREFIX} facture source n° ${sourceNum}`;
    }
  }
  text = text.replace(U3125_CREDIT_APP_REMARK_SUFFIX_REGEX, "").trim();
  return text || null;
}

/** Affiche la remarque paiement (formatée si acompte auto, sinon texte brut). */
export function u3125FormatPaymentRemarkForDisplay(remark: string | null | undefined): string {
  return u3125FormatAdvanceApplicationPaymentRemarkForDisplay(remark) ?? remark?.trim() ?? "";
}

export function u3125SubtractPaymentComponents(
  base: U3125PaymentComponentAmounts,
  subtract: U3125PaymentComponentAmounts,
): U3125PaymentComponentAmounts {
  return {
    amountTaxable: u3125RoundTaxAmount(Math.max(0, base.amountTaxable - subtract.amountTaxable)),
    amountNonTaxable: u3125RoundTaxAmount(Math.max(0, base.amountNonTaxable - subtract.amountNonTaxable)),
    taxAmount1: u3125RoundTaxAmount(Math.max(0, base.taxAmount1 - subtract.taxAmount1)),
    taxAmount2: u3125RoundTaxAmount(Math.max(0, base.taxAmount2 - subtract.taxAmount2)),
  };
}

/** Brut d’en-tête après NC (ventilation BL-0041 ou legacy) — base paiement / panneau Montants. */
function u3125NetHeaderAmountsAfterCreditApplications(
  gross: Pick<U3125InvoiceFormRow, "amountInvoiceTaxable" | "amountInvoiceNonTaxable">,
  creditApplicationsBeforeTaxTotal: number,
  creditApplications?: readonly U3125InvoiceCreditApplicationItem[],
  availableSources?: U3125CreditSource[],
): Pick<U3125InvoiceFormRow, "amountInvoiceTaxable" | "amountInvoiceNonTaxable"> {
  let taxable = u3125RoundTaxAmount(Number(gross.amountInvoiceTaxable ?? 0));
  let nonTaxable = u3125RoundTaxAmount(Number(gross.amountInvoiceNonTaxable ?? 0));
  let legacyCreditTotal = 0;

  if (creditApplications?.length) {
    for (const app of creditApplications) {
      const nature = u3125ResolveCreditApplicationNature(app, availableSources);
      if (nature !== "CREDIT") continue;
      const amount = Math.abs(Number(app.amount) || 0);
      if (amount <= 0) continue;
      const appTaxable = Math.abs(Number(app.amountTaxable) || 0);
      const appNonTaxable = Math.abs(Number(app.amountNonTaxable) || 0);
      if (appTaxable + appNonTaxable > 0.005) {
        taxable = u3125RoundTaxAmount(Math.max(0, taxable - appTaxable));
        nonTaxable = u3125RoundTaxAmount(Math.max(0, nonTaxable - appNonTaxable));
      } else {
        legacyCreditTotal = u3125RoundTaxAmount(legacyCreditTotal + amount);
      }
    }
  } else if (creditApplicationsBeforeTaxTotal > 0) {
    legacyCreditTotal = creditApplicationsBeforeTaxTotal;
  }

  if (legacyCreditTotal > 0) {
    if (legacyCreditTotal <= taxable + 0.005) {
      taxable = u3125RoundTaxAmount(taxable - legacyCreditTotal);
    } else {
      const overflow = u3125RoundTaxAmount(legacyCreditTotal - taxable);
      taxable = 0;
      nonTaxable = u3125RoundTaxAmount(Math.max(0, nonTaxable - overflow));
    }
  }

  return { amountInvoiceTaxable: taxable, amountInvoiceNonTaxable: nonTaxable };
}

/** Montants nets facture STANDARD (notes de crédit avant taxes) pour ventilation paiement. */
export function u3125NetInvoicePaymentComponents(
  row: U3125InvoiceFormRow,
  taxRates: U3125TaxRateInitItem[],
): U3125PaymentComponentAmounts {
  if (row.documentNature !== "STANDARD") {
    return u3125InvoiceToPaymentComponents(row);
  }
  const { creditApplicationsBeforeTaxTotal } = u3125SplitCreditApplicationAmounts(
    row.creditApplications ?? [],
    row.availableCreditSources,
  );
  const netHeader = u3125NetHeaderAmountsAfterCreditApplications(
    {
      amountInvoiceTaxable: Number(row.amountInvoiceTaxable ?? 0),
      amountInvoiceNonTaxable: Number(row.amountInvoiceNonTaxable ?? 0),
    },
    creditApplicationsBeforeTaxTotal,
    row.creditApplications ?? [],
    row.availableCreditSources,
  );
  const taxes = u3125RecalcHeaderTaxAmounts({ ...row, ...netHeader }, taxRates);
  return {
    amountTaxable: netHeader.amountInvoiceTaxable,
    amountNonTaxable: netHeader.amountInvoiceNonTaxable,
    taxAmount1: taxes.taxAmount1 ?? 0,
    taxAmount2: taxes.taxAmount2 ?? 0,
  };
}

/** Total des paiements auto (acomptes appliqués) déjà enregistrés sur la facture. */
export function u3125SumAdvanceAutoPaymentTotals(
  payments: readonly U3125InvoicePayment[],
  excludePaymentId?: string,
): number {
  const exclude = excludePaymentId?.trim();
  let total = 0;
  for (const payment of payments) {
    if (exclude && payment.id?.trim() === exclude) continue;
    if (!u3125IsCreditApplicationAutoPaymentRemark(payment.remark)) continue;
    total += Number(payment.amountTotal ?? 0);
  }
  return u3125RoundTaxAmount(total);
}

/** Total des paiements client (hors acomptes auto AP au RELEASE). */
export function u3125SumClientPaymentTotals(
  payments: readonly U3125InvoicePayment[],
  excludePaymentId?: string,
): number {
  const exclude = excludePaymentId?.trim();
  let total = 0;
  for (const payment of payments) {
    if (exclude && payment.id?.trim() === exclude) continue;
    if (u3125IsCreditApplicationAutoPaymentRemark(payment.remark)) continue;
    total += Number(payment.amountTotal ?? 0);
  }
  return u3125RoundTaxAmount(total);
}

/**
 * Reste à encaisser sur facture standard : `billingTotals.grandTotal` − Σ paiements client.
 */
export function u3125ComputeBalanceToCollect(
  invoice:
    | Pick<
        U3125InvoiceFormRow,
        | "documentNature"
        | "billingTotals"
        | "payments"
        | "amountInvoiceTaxable"
        | "amountInvoiceNonTaxable"
        | "taxAmount1"
        | "taxAmount2"
      >
    | null
    | undefined,
): number {
  if (!invoice || invoice.documentNature !== "STANDARD") {
    return 0;
  }
  const invoiceTotal =
    invoice.billingTotals?.invoiceTotal ??
    u3125RoundTaxAmount(
      Number(invoice.amountInvoiceTaxable ?? 0) +
        Number(invoice.amountInvoiceNonTaxable ?? 0) +
        Number(invoice.taxAmount1 ?? 0) +
        Number(invoice.taxAmount2 ?? 0),
    );
  const grandTotal = invoice.billingTotals?.grandTotal ?? invoiceTotal;
  const clientPaid = u3125SumClientPaymentTotals(invoice.payments ?? []);
  return u3125RoundTaxAmount(Math.max(0, grandTotal - clientPaid));
}

/** Total des paiements enregistrés (`amountTotal`). */
export function u3125SumPaymentAmountTotals(
  payments: readonly U3125InvoicePayment[],
  excludePaymentId?: string,
): number {
  const exclude = excludePaymentId?.trim();
  let total = 0;
  for (const payment of payments) {
    if (exclude && payment.id?.trim() === exclude) continue;
    total += Number(payment.amountTotal ?? 0);
  }
  return u3125RoundTaxAmount(total);
}

/** Montant total dû pour clôture (grand total STANDARD, en-tête TTC sinon). */
export function u3125InvoiceAmountDueForPaymentClose(
  invoice: U3125InvoiceFormRow,
  taxRates: U3125TaxRateInitItem[],
  invoiceTypes?: readonly U3125InvoiceTypeInitItem[],
): number {
  const totals = u3125ResolveBillingTotalsForDisplay(invoice, taxRates, invoiceTypes);
  return u3125RoundTaxAmount(totals.grandTotal ?? totals.invoiceTotal ?? 0);
}

/** Option B BL-0025 : Σ paiements ≥ montant dû − 0,01 $. */
export function u3125IsInvoiceFullyPaidByPayments(
  invoice: U3125InvoiceFormRow,
  taxRates: U3125TaxRateInitItem[],
  invoiceTypes?: readonly U3125InvoiceTypeInitItem[],
): boolean {
  const amountDue = u3125InvoiceAmountDueForPaymentClose(invoice, taxRates, invoiceTypes);
  const paid = u3125SumPaymentAmountTotals(invoice.payments ?? []);
  return paid >= amountDue - 0.01;
}

/**
 * Construit un paiement pour le solde restant (bouton « Marquer comme fermée », BL-0025).
 * Aligné sur le préremplissage du dialogue Ajouter (onglet Paiements).
 */
export function u3125BuildFullBalancePayment(
  invoice: U3125InvoiceFormRow,
  taxRates: U3125TaxRateInitItem[],
): U3125InvoicePayment | null {
  const remainingBalance = u3125PaymentRemainingBalanceForInvoice(
    invoice,
    invoice.payments ?? [],
    taxRates,
  );
  const amount = u3125PaymentComponentsTotal(remainingBalance);
  if (amount <= 0.005) {
    return null;
  }

  const allocated = u3125AllocatePaymentFromReceivedAmount(
    amount,
    remainingBalance,
    invoice.customerTaxable,
  );
  const paymentTypeCode =
    u3125SuggestPaymentTypeCode(invoice.documentNature, amount, amount) ?? "";

  return {
    ...emptyPaymentLine(),
    paymentTypeCode,
    amountTotal: allocated.amountTotal,
    amountTaxable: allocated.amountTaxable,
    amountNonTaxable: allocated.amountNonTaxable,
    taxAmount1: allocated.taxAmount1,
    taxAmount2: allocated.taxAmount2,
  };
}

/**
 * Solde restant pour saisie paiement — facture STANDARD :
 * total facture net (après crédits avant taxes) − paiements − acomptes après taxes non couverts par un paiement auto.
 */
export function u3125PaymentRemainingBalanceForInvoice(
  invoice: U3125InvoiceFormRow,
  payments: readonly U3125InvoicePayment[],
  taxRates: U3125TaxRateInitItem[],
  excludePaymentId?: string,
): U3125PaymentComponentAmounts {
  if (invoice.documentNature !== "STANDARD") {
    return u3125PaymentRemainingBalance(invoice, payments, excludePaymentId);
  }

  const montants = u3125ResolveMontantsPanelTotals(invoice, taxRates);
  if (!montants) {
    return u3125PaymentRemainingBalance(invoice, payments, excludePaymentId);
  }

  const paidTotal = u3125SumPaymentAmountTotals(payments, excludePaymentId);
  const advanceAutoPaid = u3125SumAdvanceAutoPaymentTotals(payments, excludePaymentId);
  const uncoveredAdvance = u3125RoundTaxAmount(Math.max(0, montants.advanceApplicationsTotal - advanceAutoPaid));
  const remainingTotal = u3125RoundTaxAmount(
    Math.max(0, montants.invoiceTotal - paidTotal - uncoveredAdvance),
  );

  if (remainingTotal <= 0.005) {
    return { amountTaxable: 0, amountNonTaxable: 0, taxAmount1: 0, taxAmount2: 0 };
  }

  const netBase = u3125NetInvoicePaymentComponents(invoice, taxRates);
  let components = u3125PaymentComponentsPoolAfterPayments(
    netBase,
    payments,
    invoice.customerTaxable,
    excludePaymentId,
  );

  if (uncoveredAdvance > 0.005) {
    const advanceSlice = u3125AllocatePaymentFromReceivedAmount(
      uncoveredAdvance,
      components,
      invoice.customerTaxable,
    );
    components = u3125SubtractPaymentComponents(components, advanceSlice);
  }

  const componentTotal = u3125PaymentComponentsTotal(components);
  if (Math.abs(componentTotal - remainingTotal) <= 0.05 && componentTotal > 0.005) {
    return components;
  }

  const ventilationBase =
    u3125PaymentComponentsTotal(components) > 0.005 ? components : netBase;
  const allocated = u3125AllocatePaymentFromReceivedAmount(
    remainingTotal,
    ventilationBase,
    invoice.customerTaxable,
  );
  return {
    amountTaxable: allocated.amountTaxable,
    amountNonTaxable: allocated.amountNonTaxable,
    taxAmount1: allocated.taxAmount1,
    taxAmount2: allocated.taxAmount2,
  };
}

/**
 * Ventile un montant reçu sur imposable / non imposable / taxes selon le solde restant (prorata ou reliquat exact).
 * Client non taxable → 100 % non imposable, taxes nulles.
 */
export function u3125AllocatePaymentFromReceivedAmount(
  receivedAmount: number,
  remaining: U3125PaymentComponentAmounts,
  customerTaxable: boolean | undefined,
): U3125AllocatedPaymentAmounts {
  const received = u3125RoundTaxAmount(Number(receivedAmount ?? 0));
  if (received <= 0) {
    return { amountTaxable: 0, amountNonTaxable: 0, taxAmount1: 0, taxAmount2: 0, amountTotal: 0 };
  }

  if (customerTaxable === false) {
    return {
      amountTaxable: 0,
      amountNonTaxable: received,
      taxAmount1: 0,
      taxAmount2: 0,
      amountTotal: received,
    };
  }

  const remainingTotal = u3125PaymentComponentsTotal(remaining);
  if (remainingTotal <= 0) {
    return { amountTaxable: 0, amountNonTaxable: 0, taxAmount1: 0, taxAmount2: 0, amountTotal: received };
  }

  /** Paiement intégral (solde restant) — reliquat exact composante par composante. */
  if (received >= remainingTotal - 0.005) {
    return {
      amountTaxable: remaining.amountTaxable,
      amountNonTaxable: remaining.amountNonTaxable,
      taxAmount1: remaining.taxAmount1,
      taxAmount2: remaining.taxAmount2,
      amountTotal: remainingTotal,
    };
  }

  /** Paiement partiel — prorata sur le solde restant ; écart d'arrondi sur taxAmount2. */
  const ratio = received / remainingTotal;
  let amountTaxable = u3125RoundTaxAmount(remaining.amountTaxable * ratio);
  let amountNonTaxable = u3125RoundTaxAmount(remaining.amountNonTaxable * ratio);
  let taxAmount1 = u3125RoundTaxAmount(remaining.taxAmount1 * ratio);
  let taxAmount2 = u3125RoundTaxAmount(remaining.taxAmount2 * ratio);
  const computedTotal = u3125RoundTaxAmount(amountTaxable + amountNonTaxable + taxAmount1 + taxAmount2);
  const diff = u3125RoundTaxAmount(received - computedTotal);
  if (diff !== 0) {
    taxAmount2 = u3125RoundTaxAmount(taxAmount2 + diff);
  }

  return {
    amountTaxable,
    amountNonTaxable,
    taxAmount1,
    taxAmount2,
    amountTotal: received,
  };
}

export function u3125PaymentAmountWithinRemaining(
  receivedAmount: number,
  remaining: U3125PaymentComponentAmounts,
): boolean {
  const received = u3125RoundTaxAmount(receivedAmount);
  const maxTotal = u3125PaymentComponentsTotal(remaining);
  return received > 0 && received <= maxTotal + 0.005;
}

function resolveProjectInvoiceAggregates(
  source: {
    invoiceAmount?: number | null;
    invoicedAmount?: number | null;
    invoiceCount?: number | null;
    invoicedCount?: number | null;
  },
  context?: {
    fixedPrice?: boolean;
    workInProgressLines?: { amount?: number | null; invoice?: { id?: string } | null }[];
  },
): { invoiceAmount: number; invoiceCount: number } {
  const invoiceCount = Number(source.invoiceCount ?? source.invoicedCount ?? 0);

  if (context?.fixedPrice === false && context.workInProgressLines?.length) {
    return {
      invoiceAmount: u3125SumUnbilledWipSellingAmount(context.workInProgressLines),
      invoiceCount: Number.isFinite(invoiceCount) ? invoiceCount : 0,
    };
  }

  const invoiceAmount = Number(source.invoiceAmount ?? source.invoicedAmount ?? 0);
  return {
    invoiceAmount: Number.isFinite(invoiceAmount) ? invoiceAmount : 0,
    invoiceCount: Number.isFinite(invoiceCount) ? invoiceCount : 0,
  };
}

/** Somme des montants vendant (`amount`) des TEC non facturées (`invoice == null`). */
export function u3125SumUnbilledWipSellingAmount(
  lines: { amount?: number | null; invoice?: { id?: string } | null }[] | undefined,
): number {
  if (!lines?.length) return 0;
  const sum = lines.filter((line) => line.invoice == null).reduce((acc, line) => acc + Number(line.amount ?? 0), 0);
  return Math.round(sum * 100) / 100;
}

/** Somme des montants coutants (`costAmount`) des TEC non facturées (`invoice == null`). */
export function u3125SumUnbilledWipCostAmount(
  lines: { costAmount?: number | null; invoice?: { id?: string } | null }[] | undefined,
): number {
  if (!lines?.length) return 0;
  const sum = lines.filter((line) => line.invoice == null).reduce((acc, line) => acc + Number(line.costAmount ?? 0), 0);
  return Math.round(sum * 100) / 100;
}

/** Nombre de TEC non facturées sur le projet. */
export function u3125CountUnbilledWipLines(lines: { invoice?: { id?: string } | null }[] | undefined): number {
  if (!lines?.length) return 0;
  return lines.filter((line) => line.invoice == null).length;
}

/** Stat TEC restant à facturer (coutant) pour le libellé du champ `amountTec`. */
export function u3125ProjectTecRemainingStat(params: {
  wipLines?: { costAmount?: number | null; invoice?: { id?: string } | null }[];
}): { amount: number; count: number } | null {
  if (!params.wipLines?.length) return null;
  return {
    amount: u3125SumUnbilledWipCostAmount(params.wipLines),
    count: u3125CountUnbilledWipLines(params.wipLines),
  };
}

/** Restant à facturer (forfait) = montant forfaitaire − montant déjà facturé. */
export function u3125RemainingToInvoiceAmount(params: {
  fixedAmount?: number | null;
  invoiceAmount?: number | null;
}): number | null {
  const fixed = params.fixedAmount;
  const invoiced = params.invoiceAmount;
  if (fixed == null || invoiced == null) return null;
  if (!Number.isFinite(Number(fixed)) || !Number.isFinite(Number(invoiced))) return null;
  return Math.round((Number(fixed) - Number(invoiced)) * 100) / 100;
}

/**
 * Montant affiché dans le libellé projet (lecture) :
 * - forfait : restant à facturer (`fixedAmount − invoiceAmount`)
 * - horaire : total vendant TEC non facturées (`invoiceAmount` API)
 */
export function u3125ProjectInvoicingStatAmount(params: {
  fixedPrice?: boolean;
  fixedAmount?: number | null;
  invoiceAmount?: number | null;
}): number | null {
  if (params.fixedPrice === true) {
    return u3125RemainingToInvoiceAmount(params);
  }
  if (params.fixedPrice === false) {
    const amount = params.invoiceAmount;
    if (amount == null || !Number.isFinite(Number(amount))) return null;
    return Math.round(Number(amount) * 100) / 100;
  }
  return null;
}

/** Plafond facturable (restant à facturer / TEC vendant non facturées) pour validation UI. */
export function u3125InvoiceRemainingCap(
  row: Pick<U3125InvoiceFormRow, "project" | "invoiceAmount" | "workInProgressLines">,
): number | null {
  const fixedPrice = row.project?.fixedPrice;
  if (fixedPrice == null) return null;

  if (fixedPrice === true) {
    return u3125RemainingToInvoiceAmount({
      fixedAmount: row.project?.fixedAmount,
      invoiceAmount: row.invoiceAmount,
    });
  }

  const { invoiceAmount } = resolveProjectInvoiceAggregates(
    { invoiceAmount: row.invoiceAmount },
    { fixedPrice: false, workInProgressLines: row.workInProgressLines },
  );
  return Math.round(invoiceAmount * 100) / 100;
}

/**
 * BL-0654 — indique si la validation UI « montants ≤ budget restant » s’applique.
 * Levée pour STANDARD si perso active ; jamais pour radiation TEC.
 */
export function u3125ShouldApplyInvoiceRemainingCapValidation(
  allowInvoiceAboveBudget: boolean,
  documentNature: U3125InitCreateDocumentNature | undefined,
): boolean {
  if (documentNature != null && u3125IsWriteOffDocument(documentNature)) return false;
  if (allowInvoiceAboveBudget && documentNature === "STANDARD") return false;
  return true;
}

/** Vrai si la somme taxable + non taxable dépasse le plafond projet (facture, note de crédit, acompte). */
export function u3125InvoiceAmountsExceedRemaining(
  row: Pick<
    U3125InvoiceFormRow,
    "documentNature" | "project" | "invoiceAmount" | "workInProgressLines" | "amountInvoiceTaxable" | "amountInvoiceNonTaxable"
  >,
): { remaining: number } | null {
  if (u3125IsWriteOffDocument(row.documentNature)) return null;
  const cap = u3125InvoiceRemainingCap(row);
  if (cap == null) return null;

  const taxable = Number(row.amountInvoiceTaxable ?? 0);
  const nonTaxable = Number(row.amountInvoiceNonTaxable ?? 0);
  if (!Number.isFinite(taxable) || !Number.isFinite(nonTaxable)) return null;

  const subtotal = Math.round((taxable + nonTaxable) * 100) / 100;
  const maxCap = Math.max(0, cap);
  if (subtotal > maxCap + 0.005) {
    return { remaining: maxCap };
  }
  return null;
}

/** Erreurs de formulaire lorsque le sous-total dépasse le plafond projet. */
export function u3125InvoiceAmountsExceedRemainingErrors(
  row: Pick<
    U3125InvoiceFormRow,
    "documentNature" | "project" | "invoiceAmount" | "workInProgressLines" | "amountInvoiceTaxable" | "amountInvoiceNonTaxable"
  >,
  message: string,
): { amountInvoiceTaxable?: string; amountInvoiceNonTaxable?: string } {
  if (!u3125InvoiceAmountsExceedRemaining(row)) return {};
  if (row.documentNature === "ADVANCE") {
    return { amountInvoiceNonTaxable: message };
  }
  return { amountInvoiceTaxable: message, amountInvoiceNonTaxable: message };
}

/**
 * Type de facture suggéré (standard forfait/horaire) :
 * - sous-total &lt; restant à facturer → `FP` (partielle)
 * - sinon → `FF` (finale)
 */
export function u3125SuggestInvoiceTypeCodeFromAmounts(
  row: Pick<
    U3125InvoiceFormRow,
    "project" | "invoiceAmount" | "workInProgressLines" | "amountInvoiceTaxable" | "amountInvoiceNonTaxable" | "invoiceTypeCode"
  >,
  invoiceTypes: U3125InvoiceTypeInitItem[],
): string | null {
  if (row.project?.fixedPrice == null) return null;

  const currentCode = row.invoiceTypeCode?.trim() ?? "";
  const currentType = invoiceTypes.find((t) => t.code === currentCode);
  if (currentType && currentType.nature !== "STANDARD") return null;

  const hasFf = u3125FilterStandardBillingInvoiceTypes(invoiceTypes).some((t) => t.code.toUpperCase() === "FF");
  const hasFp = u3125FilterStandardBillingInvoiceTypes(invoiceTypes).some((t) => t.code.toUpperCase() === "FP");
  if (!hasFf || !hasFp) return null;

  const cap = u3125InvoiceRemainingCap(row);
  if (cap == null) return null;

  const subtotal = u3125InvoiceListSubtotal(row);
  const remaining = Math.max(0, cap);
  if (subtotal < remaining - 0.005) return "FP";
  return "FF";
}

/** Numéro déjà attribué (exclut null, vide et placeholder « 0 »). */
export function u3125HasInvoiceNumberAssigned(invoiceNumber: string | number | null | undefined): boolean {
  if (invoiceNumber == null) return false;
  const trimmed = String(invoiceNumber).trim();
  return trimmed !== "" && trimmed !== "0";
}

/** Affichage numéro de facture (null / 0 → libellé auto ; accepte W-n). */
export function u3125FormatInvoiceNumberDisplay(
  invoiceNumber: string | number | null | undefined,
  autoLabel = "—",
): string {
  if (!u3125HasInvoiceNumberAssigned(invoiceNumber)) return autoLabel;
  return String(invoiceNumber).trim();
}

/** Sous-total liste = `amountInvoiceTaxable` + `amountInvoiceNonTaxable` (hors taxes). */
export function u3125InvoiceListSubtotal(row: Pick<U3125InvoiceSearchRow, "amountInvoiceTaxable" | "amountInvoiceNonTaxable">): number {
  const taxable = Number(row.amountInvoiceTaxable ?? 0);
  const nonTaxable = Number(row.amountInvoiceNonTaxable ?? 0);
  if (!Number.isFinite(taxable) || !Number.isFinite(nonTaxable)) return 0;
  return Math.round((taxable + nonTaxable) * 100) / 100;
}

/** Somme des taxes liste = `taxAmount1` + `taxAmount2`. */
export function u3125InvoiceListTaxTotal(
  row: Pick<U3125InvoiceSearchRow, "taxAmount1" | "taxAmount2">,
): number {
  const t1 = Number(row.taxAmount1 ?? 0);
  const t2 = Number(row.taxAmount2 ?? 0);
  const a = Number.isFinite(t1) ? t1 : 0;
  const b = Number.isFinite(t2) ? t2 : 0;
  return Math.round((a + b) * 100) / 100;
}

/** Montant total liste = sous-total + taxes. */
export function u3125InvoiceListTotal(
  row: Pick<
    U3125InvoiceSearchRow,
    "amountInvoiceTaxable" | "amountInvoiceNonTaxable" | "taxAmount1" | "taxAmount2"
  >,
): number {
  return Math.round((u3125InvoiceListSubtotal(row) + u3125InvoiceListTaxTotal(row)) * 100) / 100;
}

/** Libellé facture pour dialogues de suppression (numéro + projet, sans « null »). */
export function u3125FormatInvoiceDeleteLabel(row: {
  id?: string;
  invoiceNumber?: string | number | null;
  project?: Pick<U3125InvoiceProjectSummary, "projetNumber" | "projetName" | "id"> | null;
}): string {
  const projectNumber = row.project?.projetNumber?.trim() ?? "";
  const projectName = row.project?.projetName?.trim() ?? "";
  const projectLabel = projectNumber ? (projectName ? `${projectNumber} — ${projectName}` : projectNumber) : "";

  const num = row.invoiceNumber;
  const hasInvoiceNumber = u3125HasInvoiceNumberAssigned(num);

  if (hasInvoiceNumber && projectLabel) return `${String(num).trim()} — ${projectLabel}`;
  if (hasInvoiceNumber) return String(num).trim();
  if (projectLabel) return projectLabel;
  return row.id?.trim() ?? "";
}

export function pickTaxRateLabel(rate: U3125TaxRateInitItem | undefined, languageCode: string): string {
  if (!rate) return "";
  const lang = (languageCode || "fr").slice(0, 2).toLowerCase();
  if (lang === "en" && rate.labelEn?.trim()) return rate.labelEn.trim();
  if (rate.labelFr?.trim()) return rate.labelFr.trim();
  return rate.code?.trim() ?? rate.id;
}

export function pickTaxRateComponentLabel(
  component: U3125TaxRateComponentInitItem | undefined,
  languageCode: string,
): string {
  if (!component) return "";
  const lang = (languageCode || "fr").slice(0, 2).toLowerCase();
  if (lang === "en" && component.labelEn?.trim()) return component.labelEn.trim();
  if (component.labelFr?.trim()) return component.labelFr.trim();
  return "";
}

/** Affiche un taux de taxe en pourcentage (ex. `5 %`, `9.975 %`). */
export function u3125FormatTaxRatePercent(rate: number | null | undefined): string {
  if (rate == null || !Number.isFinite(rate)) return "";
  const text =
    rate % 1 === 0
      ? String(rate)
      : rate
          .toFixed(3)
          .replace(/0+$/, "")
          .replace(/\.$/, "");
  return `${text}\u00A0%`;
}

/** Libellé composante taxe avec taux entre parenthèses (ex. « TPS (5 %) »). */
export function u3125FormatTaxComponentFieldLabel(
  component: U3125TaxRateComponentInitItem | undefined,
  languageCode: string,
  fallback: string,
): string {
  const name = pickTaxRateComponentLabel(component, languageCode) || fallback;
  if (!component) return name;
  const pct = u3125FormatTaxRatePercent(u3125ResolveTaxComponentRate(component));
  return pct ? `${name} (${pct})` : name;
}

export function u3125SortTaxRateComponents(components: U3125TaxRateComponentInitItem[]): U3125TaxRateComponentInitItem[] {
  return [...components].sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Taux composante (%) — tolère string API ou clés alternatives. */
export function u3125ResolveTaxComponentRate(component: U3125TaxRateComponentInitItem | Record<string, unknown> | undefined): number | null {
  if (!component || typeof component !== "object") return null;
  const raw = component as Record<string, unknown>;
  const candidate = raw.rate ?? raw.ratePercent ?? raw.percentage ?? raw.percent;
  if (candidate == null || candidate === "") return null;
  const n = Number(candidate);
  return Number.isFinite(n) ? n : null;
}

/** Normalise les régimes initLU (taux composantes numériques). */
export function u3125NormalizeInitLUTaxRates(taxRates: U3125TaxRateInitItem[] | undefined): U3125TaxRateInitItem[] {
  if (!taxRates?.length) return [];
  return taxRates.map((rate) => {
    const raw = rate as U3125TaxRateInitItem & { tax_rate_components?: U3125TaxRateComponentInitItem[] };
    const components = rate.taxRateComponents ?? raw.tax_rate_components ?? [];
    return {
      ...rate,
      taxRateComponents: components.map((component) => {
        const resolved = u3125ResolveTaxComponentRate(component);
        return resolved != null ? { ...component, rate: resolved } : { ...component };
      }),
    };
  });
}

export function u3125FindTaxRateByCode(
  taxRates: U3125TaxRateInitItem[],
  taxRateCode: string | null | undefined,
): U3125TaxRateInitItem | undefined {
  const code = taxRateCode?.trim();
  if (!code) return undefined;
  const upper = code.toUpperCase();
  return taxRates.find((r) => {
    const value = taxRateOptionValue(r);
    return value === code || value.toUpperCase() === upper || r.id === code;
  });
}

/** Libellés des champs taxAmount1 / taxAmount2 selon les composantes du régime sélectionné. */
export function u3125TaxAmountFieldLabels(
  taxRates: U3125TaxRateInitItem[],
  taxRateCode: string | null | undefined,
  languageCode: string,
  fallbacks: { taxAmount1: string; taxAmount2: string },
): { taxAmount1: string; taxAmount2: string } {
  const rate = u3125FindTaxRateByCode(taxRates, taxRateCode);
  const sorted = u3125SortTaxRateComponents(rate?.taxRateComponents ?? []);
  return {
    taxAmount1: u3125FormatTaxComponentFieldLabel(sorted[0], languageCode, fallbacks.taxAmount1),
    taxAmount2: u3125FormatTaxComponentFieldLabel(sorted[1], languageCode, fallbacks.taxAmount2),
  };
}

/** Libellé du régime de taxes à partir du détail renvoyé par l’endpoint GET (`taxes`). */
export function pickInvoiceTaxesLabel(taxes: U3125InvoiceTaxesRef | null | undefined, languageCode: string): string {
  if (!taxes) return "";
  const lang = (languageCode || "fr").slice(0, 2).toLowerCase();
  if (lang === "en" && taxes.labelEn?.trim()) return taxes.labelEn.trim();
  if (taxes.labelFr?.trim()) return taxes.labelFr.trim();
  return taxes.code?.trim() ?? "";
}

/** Valeur `taxRateCode` alignée sur l’initLU (code métier ou id). */
export function taxRateOptionValue(rate: U3125TaxRateInitItem): string {
  const c = rate.code?.trim();
  if (c) return c;
  return rate.id;
}

export function u3125TaxRateCodeAllowed(code: string, rates: U3125TaxRateInitItem[]): boolean {
  const t = code.trim();
  if (!t) return false;
  return rates.some((r) => taxRateOptionValue(r) === t);
}

export const emptyPaymentLine = (): U3125InvoicePayment => ({
  paymentDate: u2000TodayOrgYmd(),
  paymentTypeCode: "",
  amountTotal: 0,
  amountTaxable: 0,
  amountNonTaxable: 0,
  taxAmount1: null,
  taxAmount2: null,
  remark: "",
});

export const emptyInvoiceLine = (position: number): U3125InvoiceLineItem => ({
  position,
  kind: "LABOR",
  description: "",
  quantity: null,
  unitPrice: null,
  amount: 0,
  taxable: true,
  wipAmount: null,
  wipLineIds: [],
  appliedFromInvoiceId: null,
});

export const emptyInvoiceForm = (): U3125InvoiceFormRow => ({
  id: "",
  version: 0,
  useAutoInvoiceNumber: true,
  invoiceNumber: null,
  invoiceDate: u2000TodayOrgYmd(),
  status: "OPEN",
  documentNature: "STANDARD",
  invoiceTypeCode: "",
  taxRateCode: "",
  taxes: null,
  customerTaxable: true,
  country: emptyU3125InvoiceFormCountry(),
  province: emptyU3125InvoiceFormProvince(),
  amountTec: 0,
  amountInvoiceTaxable: 0,
  amountInvoiceNonTaxable: 0,
  taxAmount1: null,
  taxAmount2: null,
  totalsOverride: false,
  originalInvoiceId: "",
  remark: "",
  project: null,
  workInProgressLineIds: [],
  lines: [],
  payments: [],
  invoiceAmount: 0,
  invoiceCount: 0,
  customer: undefined,
  availableCreditSources: [],
  creditApplications: [],
  billingTotals: null,
  sourceUsageSummary: null,
  availableCreditAlert: null,
});

export const emptyCreditApplication = (position: number): U3125InvoiceCreditApplicationGridRow => ({
  id: "",
  position,
  sourceInvoiceId: "",
  amount: 0,
  amountTaxable: 0,
  amountNonTaxable: 0,
});

function mapInvoiceCountryFromApi(inv: U3125Invoice): U3125InvoiceFormCountry {
  const nested = inv.country;
  const idRaw = nested?.id ?? inv.countryId ?? null;
  const id = idRaw && isValidId(String(idRaw)) ? String(idRaw) : null;
  if (!id) return emptyU3125InvoiceFormCountry();
  return {
    id,
    countryNameFr: nested?.countryNameFr ?? "",
    countryNameEn: nested?.countryNameEn ?? "",
    countryName: nested?.countryName ?? "",
    name: nested?.countryNameFr ?? nested?.countryNameEn ?? nested?.countryName ?? "",
  };
}

function mapInvoiceProvinceFromApi(inv: U3125Invoice): U3125InvoiceFormProvince {
  const nested = inv.province;
  const idRaw = nested?.id ?? inv.provinceId ?? null;
  const id = idRaw && isValidId(String(idRaw)) ? String(idRaw) : null;
  if (!id) return emptyU3125InvoiceFormProvince();
  return {
    id,
    stateNameFr: nested?.stateNameFr ?? "",
    stateNameEn: nested?.stateNameEn ?? "",
    stateName: nested?.stateName ?? "",
    name: nested?.stateNameFr ?? nested?.stateNameEn ?? nested?.stateName ?? "",
  };
}

export function mapInvoiceToFormRow(inv: U3125Invoice): U3125InvoiceFormRow {
  const { invoiceAmount, invoiceCount } = resolveProjectInvoiceAggregates(inv, {
    fixedPrice: inv.project?.fixedPrice,
    workInProgressLines: inv.workInProgressLines,
  });
  const headerAmounts = u3125ResolveGrossHeaderAmountsFromInvoice(inv);
  return {
    id: inv.id,
    version: inv.version ?? 0,
    useAutoInvoiceNumber: true,
    invoiceNumber: inv.invoiceNumber,
    invoiceDate: inv.invoiceDate,
    status: inv.status ?? "OPEN",
    documentNature: inv.documentNature ?? "STANDARD",
    invoiceTypeCode: inv.invoiceTypeCode,
    taxRateCode: inv.taxRateCode,
    taxes: inv.taxes
      ? {
          code: inv.taxes.code,
          labelFr: inv.taxes.labelFr ?? null,
          labelEn: inv.taxes.labelEn ?? null,
        }
      : null,
    customerTaxable: inv.customerTaxable ?? inv.customer?.taxable !== false,
    country: mapInvoiceCountryFromApi(inv),
    province: mapInvoiceProvinceFromApi(inv),
    amountTec: inv.amountTec,
    amountInvoiceTaxable: headerAmounts.amountInvoiceTaxable,
    amountInvoiceNonTaxable: headerAmounts.amountInvoiceNonTaxable,
    taxAmount1: inv.taxAmount1 ?? null,
    taxAmount2: inv.taxAmount2 ?? null,
    totalsOverride: inv.totalsOverride ?? false,
    originalInvoiceId: inv.originalInvoiceId?.trim() ?? "",
    remark: inv.remark ?? "",
    project: mapProjectSummaryFromApi(inv.project),
    workInProgressLineIds: [...(inv.workInProgressLineIds ?? [])],
    workInProgressLines: parseWipTransactionsFromResult(inv),
    lines: u3125NonCreditApplicationLines(inv.lines ?? []).map((l) => ({
      ...l,
      wipLineIds: [...(l.wipLineIds ?? [])],
    })),
    payments: (inv.payments ?? []).map((p) => ({ ...p })),
    invoiceAmount,
    invoiceCount,
    customer: inv.customer?.id ? { ...inv.customer } : undefined,
    availableCreditSources: [],
    creditApplications: (inv.creditApplications ?? []).map((app) => ({ ...app })),
    billingTotals: inv.billingTotals ?? null,
    sourceUsageSummary: mapSourceUsageSummaryFromApi(inv.sourceUsageSummary),
    availableCreditAlert: mapAvailableCreditAlertFromApi(inv.availableCreditAlert),
  };
}

export function mapSourceUsageSummaryFromApi(raw: unknown): U3125SourceUsageSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const summary = raw as U3125SourceUsageSummary;
  return {
    originalAmount: Number(summary.originalAmount) || 0,
    appliedAmount: Number(summary.appliedAmount) || 0,
    remainingAmount: Number(summary.remainingAmount) || 0,
    fullyConsumed: Boolean(summary.fullyConsumed),
    consumptions: (summary.consumptions ?? []).map((item) => ({
      targetInvoiceId: String(item.targetInvoiceId ?? ""),
      targetInvoiceNumber: item.targetInvoiceNumber ?? null,
      targetInvoiceDate: item.targetInvoiceDate ?? "",
      targetInvoiceStatus: (item.targetInvoiceStatus ?? "OPEN") as U3125InvoiceStatus,
      amount: Math.abs(Number(item.amount) || 0),
    })),
  };
}

/** Valeur absolue pour affichage CREDIT ; valeur brute pour ADVANCE (TTC). */
export function u3125FormatBillingSourceDisplayAmount(
  value: number | null | undefined,
  nature: "CREDIT" | "ADVANCE" | U3125InvoiceTypeNature,
): number {
  const n = Number(value) || 0;
  return nature === "CREDIT" ? Math.abs(n) : n;
}

export function u3125SourceUsageConsumedAmount(summary: U3125SourceUsageSummary): number {
  return Math.abs(Number(summary.appliedAmount) || 0);
}

export function u3125SourceUsageOriginalAbs(summary: U3125SourceUsageSummary): number {
  return Math.abs(Number(summary.originalAmount) || 0);
}

export function u3125ResolveSourceUsageStatus(summary: U3125SourceUsageSummary): U3125SourceUsageStatus {
  if (summary.fullyConsumed) return "FULL";
  if (u3125SourceUsageConsumedAmount(summary) <= 0.005) return "UNUSED";
  return "PARTIAL";
}

export function u3125SourceUsageProgressPercent(summary: U3125SourceUsageSummary): number {
  const original = u3125SourceUsageOriginalAbs(summary);
  if (original <= 0) return 0;
  const consumed = u3125SourceUsageConsumedAmount(summary);
  return Math.min(100, Math.round((consumed / original) * 1000) / 10);
}

function mapProjectSummaryFromApi(project: U3125Invoice["project"] | U3125InitCreateProject | null | undefined): U3125InvoiceProjectSummary | null {
  if (!project?.id) return null;
  return {
    id: project.id,
    projetNumber: project.projetNumber,
    projetName: project.projetName,
    fixedPrice: project.fixedPrice,
    fixedAmount: project.fixedAmount,
    hourlyMarkup: project.hourlyMarkup,
    sellingRate: project.sellingRate,
    billingFrequency: project.billingFrequency ?? null,
    subProjects: project.subProjects,
  };
}

function appendInvoiceJurisdictionFields(base: Record<string, unknown>, row: U3125InvoiceFormRow): void {
  const countryId = row.country?.id?.trim();
  const provinceId = row.province?.id?.trim();
  if (countryId) base.countryId = countryId;
  if (provinceId) base.provinceId = provinceId;
}

function appendInvoiceTaxRateField(base: Record<string, unknown>, row: U3125InvoiceFormRow): void {
  if (u3125IsAdvanceInvoice(row) || u3125IsWriteOffDocument(row.documentNature)) {
    const code = row.taxRateCode?.trim();
    if (code) base.taxRateCode = code;
    return;
  }
  if (row.customerTaxable !== false && row.taxRateCode?.trim()) {
    base.taxRateCode = row.taxRateCode.trim();
  }
}

function normalizeInvoiceDate(value: string): string {
  const ymd = U2000ApiValueToDisplayYmd(value);
  if (ymd) return ymd;
  return value.trim();
}

function normalizePaymentDate(iso: string): string {
  return normalizeInvoiceDate(iso);
}

function mapLineToWriteBody(line: U3125InvoiceLineItem, idx: number): Record<string, unknown> {
  const base: Record<string, unknown> = {
    position: line.position ?? idx,
    kind: line.kind,
    description: line.description.trim(),
    quantity: line.quantity ?? undefined,
    unitPrice: line.unitPrice ?? undefined,
    amount: line.amount,
    taxable: line.taxable,
    wipAmount: line.wipAmount ?? undefined,
    wipLineIds: line.wipLineIds ?? [],
    appliedFromInvoiceId: line.appliedFromInvoiceId?.trim() || undefined,
  };
  if (line.version != null && line.version !== undefined && line.id?.trim()) {
    base.id = line.id.trim();
    base.version = line.version;
  }
  return base;
}

function mapPaymentToWriteBody(p: U3125InvoicePayment): Record<string, unknown> {
  const base: Record<string, unknown> = {
    paymentDate: normalizePaymentDate(p.paymentDate),
    paymentTypeCode: p.paymentTypeCode.trim(),
    amountTotal: p.amountTotal,
    amountTaxable: p.amountTaxable,
    amountNonTaxable: p.amountNonTaxable,
    taxAmount1: p.taxAmount1,
    taxAmount2: p.taxAmount2,
    remark: p.remark?.trim() || undefined,
  };
  if (p.version != null && p.version !== undefined && p.id?.trim()) {
    base.id = p.id.trim();
    base.version = p.version;
  }
  return base;
}

function mapAllLinesToWrite(lines: U3125InvoiceLineItem[]): Record<string, unknown>[] {
  return u3125NonCreditApplicationLines(lines).map((line, idx) => mapLineToWriteBody(line, idx));
}

function mapCreditApplicationToWriteBody(app: U3125InvoiceCreditApplicationItem, idx: number): Record<string, unknown> {
  const nature = app.sourceNature;
  const amount = Math.abs(Number(app.amount) || 0);
  const base: Record<string, unknown> = {
    position: app.position ?? idx,
    sourceInvoiceId: app.sourceInvoiceId.trim(),
    amount,
  };
  if (nature === "CREDIT") {
    const amountTaxable = Math.abs(Number(app.amountTaxable) || 0);
    const amountNonTaxable = Math.abs(Number(app.amountNonTaxable) || 0);
    if (amountTaxable + amountNonTaxable > 0.005) {
      base.amountTaxable = amountTaxable;
      base.amountNonTaxable = amountNonTaxable;
      base.amount = u3125RoundTaxAmount(amountTaxable + amountNonTaxable);
    }
  }
  if (app.id?.trim() && isValidId(app.id.trim()) && app.version != null) {
    base.id = app.id.trim();
    base.version = app.version;
  }
  return base;
}

export function mapCreditApplicationsToWrite(applications: U3125InvoiceCreditApplicationItem[]): Record<string, unknown>[] {
  return applications
    .filter((app) => {
      if (!app.sourceInvoiceId?.trim()) return false;
      const amount = Math.abs(Number(app.amount) || 0);
      if (amount <= 0) return false;
      if (app.sourceNature === "CREDIT" && !u3125IsLegacyCreditApplication(app)) {
        const taxable = Math.abs(Number(app.amountTaxable) || 0);
        const nonTaxable = Math.abs(Number(app.amountNonTaxable) || 0);
        if (taxable + nonTaxable <= 0.005) return false;
      }
      return true;
    })
    .map((app, idx) => mapCreditApplicationToWriteBody(app, idx));
}

/**
 * Corps POST /U3125/create.
 * Pas de `companyCode` : le backend applique `resolveCompanyCodeFromProjectName(projetName)` si absent.
 */
/** Recalcule amountTec = somme des montants coutants (`costAmount`) des TEC sélectionnées. */
export function u3125RecalcAmountTecFromWipLines(
  lines: Pick<U3125InitCreateWipLine, "selected" | "costAmount">[],
): number {
  return u3125RecalcInvoiceAmountsFromWipSelection({
    fixedPrice: false,
    customerTaxable: true,
    alreadyInvoiced: 0,
    fixedAmount: 0,
    wipLines: lines.map((line) => ({ ...line, amount: 0, billable: true })),
  }).amountTec;
}

/** Arrondi monétaire taxes (aligné backend U3125). */
export function u3125RoundTaxAmount(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Calcule taxAmount1 / taxAmount2 à partir de la base imposable et des composantes initLU. */
export function u3125ComputeTaxAmountsFromTaxableBase(
  taxableBase: number,
  components: Pick<U3125TaxRateComponentInitItem, "sortOrder" | "rate" | "applicationMode">[],
): { taxAmount1: number | null; taxAmount2: number | null } {
  if (!Number.isFinite(taxableBase)) {
    return { taxAmount1: null, taxAmount2: null };
  }
  const sorted = u3125SortTaxRateComponents(components as U3125TaxRateComponentInitItem[])
    .map((component) => ({ component, rate: u3125ResolveTaxComponentRate(component) }))
    .filter((entry): entry is { component: U3125TaxRateComponentInitItem; rate: number } => entry.rate != null);
  if (sorted.length === 0) {
    return { taxAmount1: null, taxAmount2: null };
  }
  const r1 = sorted[0].rate;
  const t1 = u3125RoundTaxAmount((taxableBase * r1) / 100);
  if (sorted.length === 1) {
    return { taxAmount1: t1, taxAmount2: null };
  }
  const r2 = sorted[1].rate;
  const t2 =
    sorted[1].component.applicationMode === "MultiplicativeOnPrevious"
      ? u3125RoundTaxAmount(((taxableBase + t1) * r2) / 100)
      : u3125RoundTaxAmount((taxableBase * r2) / 100);
  return { taxAmount1: t1, taxAmount2: t2 };
}

/** Acompte : pas de taxes sur le document. */
export function u3125DocumentNatureCalculatesInvoiceTaxes(documentNature: U3125InitCreateDocumentNature): boolean {
  return documentNature !== "ADVANCE" && !u3125IsWriteOffDocument(documentNature);
}

/** TEC et onglet travaux en cours : facture standard et radiation TEC. */
export function u3125DocumentNatureUsesWipLines(documentNature: U3125InitCreateDocumentNature): boolean {
  return documentNature === "STANDARD" || u3125IsWriteOffDocument(documentNature);
}

/** Onglet / flux détail TEC : nature WIP + action `U3125:VIEWTECLINES` (BL-0618). */
export function u3125ShowInvoiceWipLinesTab(
  documentNature: U3125InitCreateDocumentNature,
  canViewTecLines: boolean,
): boolean {
  return u3125DocumentNatureUsesWipLines(documentNature) && canViewTecLines;
}

/** Facture acompte (`documentNature` ou type de facture de nature ADVANCE). */
export function u3125IsAdvanceInvoice(
  row: Pick<U3125InvoiceFormRow, "documentNature" | "invoiceTypeCode">,
  invoiceTypes?: readonly U3125InvoiceTypeInitItem[],
): boolean {
  if (row.documentNature === "ADVANCE") return true;
  const code = row.invoiceTypeCode?.trim();
  if (!code || !invoiceTypes?.length) return false;
  return invoiceTypes.find((t) => t.code === code)?.nature === "ADVANCE";
}

/** Code taxes obligatoire en validation UI (exclut client non taxable et acompte). */
export function u3125InvoiceRequiresTaxRateCode(
  row: Pick<U3125InvoiceFormRow, "customerTaxable" | "documentNature" | "invoiceTypeCode">,
  invoiceTypes?: readonly U3125InvoiceTypeInitItem[],
): boolean {
  if (row.customerTaxable === false) return false;
  if (u3125IsWriteOffDocument(row.documentNature)) return false;
  return !u3125IsAdvanceInvoice(row, invoiceTypes);
}

/** Recalcule taxAmount1 / taxAmount2 de l'en-tête facture selon le régime et la base imposable. */
export function u3125RecalcHeaderTaxAmounts(
  row: Pick<U3125InvoiceFormRow, "customerTaxable" | "taxRateCode" | "amountInvoiceTaxable" | "documentNature">,
  taxRates: U3125TaxRateInitItem[],
): { taxAmount1: number | null; taxAmount2: number | null } {
  if (row.documentNature === "ADVANCE" || u3125IsWriteOffDocument(row.documentNature)) {
    return { taxAmount1: null, taxAmount2: null };
  }
  if (row.customerTaxable === false || !row.taxRateCode?.trim()) {
    return { taxAmount1: null, taxAmount2: null };
  }
  const rate = u3125FindTaxRateByCode(taxRates, row.taxRateCode);
  return u3125ComputeTaxAmountsFromTaxableBase(Number(row.amountInvoiceTaxable ?? 0), rate?.taxRateComponents ?? []);
}

/**
 * Recalcule amountTec + montants facture selon la sélection TEC (aligné `u3125ComputeInitInvoiceAmounts` backend).
 */
export function u3125RecalcInvoiceAmountsFromWipSelection(params: {
  fixedPrice: boolean;
  customerTaxable: boolean;
  alreadyInvoiced: number;
  fixedAmount: number;
  wipLines: Pick<U3125InitCreateWipLine, "selected" | "amount" | "costAmount" | "billable">[];
}): { amountTec: number; amountInvoiceTaxable: number; amountInvoiceNonTaxable: number } {
  let amountTec = 0;
  for (const line of params.wipLines) {
    if (!line.selected) continue;
    const cost = Number(line.costAmount ?? 0);
    if (Number.isFinite(cost)) amountTec += cost;
  }
  amountTec = Math.round(amountTec * 100) / 100;

  if (params.fixedPrice) {
    const remaining = Math.max(0, params.fixedAmount - params.alreadyInvoiced);
    const rounded = Math.round(remaining * 100) / 100;
    if (params.customerTaxable) {
      return { amountTec, amountInvoiceTaxable: rounded, amountInvoiceNonTaxable: 0 };
    }
    return { amountTec, amountInvoiceTaxable: 0, amountInvoiceNonTaxable: rounded };
  }

  let amountInvoiceTaxable = 0;
  let amountInvoiceNonTaxable = 0;
  for (const line of params.wipLines) {
    if (!line.selected) continue;
    const amt = Number(line.amount ?? 0);
    if (!Number.isFinite(amt)) continue;
    if (params.customerTaxable && line.billable) {
      amountInvoiceTaxable += amt;
    } else {
      amountInvoiceNonTaxable += amt;
    }
  }
  return {
    amountTec,
    amountInvoiceTaxable: Math.round(amountInvoiceTaxable * 100) / 100,
    amountInvoiceNonTaxable: Math.round(amountInvoiceNonTaxable * 100) / 100,
  };
}

/** Applique une sélection TEC au formulaire facture. */
export function u3125ApplyWipSelectionToFormRow(
  row: U3125InvoiceFormRow,
  wipLines: Pick<U3134WorkInProgressSearchRow, "id" | "amount" | "costAmount" | "billable">[],
  selectedIds: string[],
  taxRates: U3125TaxRateInitItem[],
): U3125InvoiceFormRow {
  const idSet = new Set(selectedIds);
  const wipLinesForCalc = wipLines.map((line) => ({
    selected: idSet.has(line.id),
    amount: line.amount,
    costAmount: line.costAmount,
    billable: line.billable === true,
  }));

  const amountTec = u3125RecalcAmountTecFromWipLines(wipLinesForCalc);

  // Forfait facture standard : la sélection TEC ne modifie que amountTec + workInProgressLineIds (montants facture saisissables).
  if (row.project?.fixedPrice === true && !u3125IsWriteOffDocument(row.documentNature)) {
    return {
      ...row,
      workInProgressLineIds: [...selectedIds],
      amountTec,
    };
  }

  const amounts = u3125RecalcInvoiceAmountsFromWipSelection({
    fixedPrice: false,
    customerTaxable: row.customerTaxable !== false,
    alreadyInvoiced: row.invoiceAmount ?? 0,
    fixedAmount: row.project?.fixedAmount ?? 0,
    wipLines: wipLinesForCalc,
  });

  let next: U3125InvoiceFormRow = {
    ...row,
    workInProgressLineIds: [...selectedIds],
    ...amounts,
  };

  if (
    next.customerTaxable !== false &&
    next.documentNature !== "ADVANCE" &&
    !u3125IsWriteOffDocument(next.documentNature) &&
    next.taxRateCode?.trim()
  ) {
    const taxes = u3125RecalcHeaderTaxAmounts(next, taxRates);
    next = { ...next, taxAmount1: taxes.taxAmount1, taxAmount2: taxes.taxAmount2 };
  } else {
    next = { ...next, taxAmount1: null, taxAmount2: null };
  }

  return next;
}

export function mapInitPreviewToFormRow(
  preview: U3125InitCreateNewInvoice,
  taxRates: U3125TaxRateInitItem[],
): { row: U3125InvoiceFormRow; wipCandidates: U3134WorkInProgressSearchRow[] } {
  const countryId = preview.country?.id?.trim();
  const provinceId = preview.province?.id?.trim();
  const { invoiceAmount, invoiceCount } = resolveProjectInvoiceAggregates(preview, {
    fixedPrice: preview.project?.fixedPrice,
    workInProgressLines: preview.workInProgressLines,
  });
  const row: U3125InvoiceFormRow = {
    ...emptyInvoiceForm(),
    id: "",
    version: 0,
    useAutoInvoiceNumber: true,
    invoiceNumber: null,
    invoiceDate: U2000ApiValueToDisplayYmd(preview.invoiceDate) ?? preview.invoiceDate.slice(0, 10),
    status: preview.status,
    documentNature: preview.documentNature,
    invoiceTypeCode: preview.invoiceTypeCode,
    taxRateCode: preview.taxRateCode?.trim() ?? "",
    taxes: null,
    customerTaxable: preview.customer.taxable !== false,
    country: countryId ? { ...emptyU3125InvoiceFormCountry(), id: countryId } : emptyU3125InvoiceFormCountry(),
    province: provinceId ? { ...emptyU3125InvoiceFormProvince(), id: provinceId } : emptyU3125InvoiceFormProvince(),
    amountTec: preview.amountTec,
    amountInvoiceTaxable: preview.amountInvoiceTaxable,
    amountInvoiceNonTaxable: preview.amountInvoiceNonTaxable,
    taxAmount1: preview.taxAmount1 ?? null,
    taxAmount2: preview.taxAmount2 ?? null,
    totalsOverride: false,
    originalInvoiceId: "",
    originalInvoiceCandidates: preview.originalInvoiceCandidates,
    remark: "",
    project: preview.project?.id ? mapProjectSummaryFromApi(preview.project) : null,
    workInProgressLineIds: [...preview.workInProgressLineIds],
    lines: [],
    payments: [],
    invoiceAmount,
    invoiceCount,
    customer: preview.customer?.id ? { ...preview.customer } : undefined,
    availableCreditSources: [],
    creditApplications: [],
    billingTotals: null,
    sourceUsageSummary: null,
    availableCreditAlert: mapAvailableCreditAlertFromApi(preview.availableCreditAlert),
  };
  if (!row.taxRateCode && row.customerTaxable && row.documentNature !== "ADVANCE" && !u3125IsWriteOffDocument(row.documentNature)) {
    const selectable = u3125FilterTaxRatesForInvoiceSelection(taxRates, {
      customerTaxable: row.customerTaxable,
      invoiceCountryId: row.country?.id,
      invoiceProvinceId: row.province?.id,
    });
    if (selectable.length === 1) {
      row.taxRateCode = taxRateOptionValue(selectable[0]);
    }
  }
  const wipCandidates: U3134WorkInProgressSearchRow[] = preview.workInProgressLines.map(({ selected: _s, ...line }) =>
    normalizeU3125WipTransactionLine(line as unknown as Record<string, unknown>),
  );
  row.workInProgressLines = wipCandidates;
  return { row, wipCandidates };
}

export type U3125InvoiceSaveOptions = {
  releaseAfterCreate?: boolean;
};

export function buildCreateBody(row: U3125InvoiceFormRow, options?: U3125InvoiceSaveOptions): Record<string, unknown> {
  if (!row.project?.id) {
    throw new Error("projectId required");
  }
  const base: Record<string, unknown> = {
    projectId: row.project.id,
    useAutoInvoiceNumber: true,
    invoiceDate: normalizeInvoiceDate(row.invoiceDate),
    /** Création : statut non modifiable en UI — toujours OPEN (aligné livrable). */
    status: "OPEN",
    documentNature: row.documentNature,
    invoiceTypeCode: row.invoiceTypeCode.trim(),
    amountTec: row.amountTec,
    amountInvoiceTaxable: row.amountInvoiceTaxable,
    amountInvoiceNonTaxable: row.amountInvoiceNonTaxable,
    taxAmount1: row.taxAmount1,
    taxAmount2: row.taxAmount2,
    remark: row.remark?.trim() || undefined,
    workInProgressLineIds: row.workInProgressLineIds,
    totalsOverride: row.totalsOverride,
    lines: mapAllLinesToWrite(row.lines),
  };
  appendInvoiceTaxRateField(base, row);
  appendInvoiceJurisdictionFields(base, row);
  if (row.originalInvoiceId?.trim() && !u3125IsWriteOffDocument(row.documentNature)) {
    base.originalInvoiceId = row.originalInvoiceId.trim();
  }
  if (row.documentNature === "STANDARD") {
    base.creditApplications = mapCreditApplicationsToWrite(row.creditApplications ?? []);
  }
  base.releaseAfterCreate = options?.releaseAfterCreate ?? false;
  return base;
}

/** Corps POST /U3125/update/:id — pas de companyCode (non géré par le backend update). */
export function buildUpdateBody(row: U3125InvoiceFormRow, options?: U3125InvoiceSaveOptions): Record<string, unknown> {
  if (!row.project?.id) {
    throw new Error("projectId required");
  }
  const base: Record<string, unknown> = {
    id: row.id,
    version: row.version,
    projectId: row.project.id,
    useAutoInvoiceNumber: true,
    invoiceDate: normalizeInvoiceDate(row.invoiceDate),
    invoiceTypeCode: row.invoiceTypeCode.trim(),
    amountTec: row.amountTec,
    amountInvoiceTaxable: row.amountInvoiceTaxable,
    amountInvoiceNonTaxable: row.amountInvoiceNonTaxable,
    taxAmount1: row.taxAmount1,
    taxAmount2: row.taxAmount2,
    remark: row.remark?.trim() || undefined,
    workInProgressLineIds: row.workInProgressLineIds,
    totalsOverride: row.totalsOverride,
    originalInvoiceId: u3125IsWriteOffDocument(row.documentNature) ? undefined : row.originalInvoiceId?.trim() || undefined,
    lines: mapAllLinesToWrite(row.lines),
  };
  appendInvoiceTaxRateField(base, row);
  appendInvoiceJurisdictionFields(base, row);
  if (row.documentNature === "STANDARD") {
    base.creditApplications = mapCreditApplicationsToWrite(row.creditApplications ?? []);
  }
  base.releaseAfterCreate = options?.releaseAfterCreate ?? false;
  return base;
}

export function buildPaymentCreateBody(
  invoice: Pick<U3125InvoiceFormRow, "id" | "version">,
  payment: U3125InvoicePayment,
): Record<string, unknown> {
  return {
    invoiceId: invoice.id,
    invoiceVersion: invoice.version,
    ...mapPaymentToWriteBody(payment),
  };
}

export function buildPaymentUpdateBody(payment: U3125InvoicePayment): Record<string, unknown> {
  if (!payment.id?.trim()) {
    throw new Error("payment id required");
  }
  return {
    id: payment.id.trim(),
    version: payment.version ?? 0,
    ...mapPaymentToWriteBody(payment),
  };
}

export function buildPaymentDeleteBody(payment: Pick<U3125InvoicePayment, "id" | "version">): {
  id: string;
  version: number;
} {
  if (!payment.id?.trim()) {
    throw new Error("payment id required");
  }
  return { id: payment.id.trim(), version: payment.version ?? 0 };
}

export const doInitCreateNewInvoice = (axios: AxiosInstance, body: U3125InitCreateNewInvoiceRequest) => {
  return axios.post<{ data: U3125InitCreateNewInvoice }>(LogicalUnitEndpoints.INIT_CREATE_NEW_INVOICE, body);
};

/** Tag étape 1 wizard forfait — ordre d’ajout conservé pour preview. */
export type U3125ForfaitWizardProjectTag = {
  projetNumber: string;
  projectId?: string;
  projetName?: string;
  customerName?: string;
  freeText?: boolean;
};

export type U3125ForfaitWizardPreviewLineStatus =
  | "ok"
  | "not_found"
  | "not_fixed_price"
  | "not_billable"
  | "draft_invoice_exists"
  | "billing_complete";

export type U3125ForfaitWizardPreviewRequest = {
  projectNumbers: string[];
};

export type U3125ForfaitWizardPreviewLineOk = {
  inputProjectNumber: string;
  status: "ok";
  projectId: string;
  projetNumber: string;
  projetName?: string;
  customer?: { id: string; customerCode?: string; customerName?: string; taxable?: boolean };
  fixedAmount?: number;
  amountAlreadyInvoiced?: number;
  amountTecToInvoice?: number;
  remainingBudget?: number;
  suggestedInvoiceAmount?: number;
  suggestedInvoiceTypeCode?: string;
  partialInvoicesReleasedCount?: number;
  billingFrequency?: string | null;
  workInProgressLineCount?: number;
  countryId?: string | null;
  provinceId?: string | null;
  suggestedTaxRateCode?: string | null;
  suggestedTaxAmount1?: number;
  suggestedTaxAmount2?: number;
};

export type U3125ForfaitWizardPreviewLineError = {
  inputProjectNumber: string;
  status: Exclude<U3125ForfaitWizardPreviewLineStatus, "ok">;
  errorMessage?: string;
};

export type U3125ForfaitWizardPreviewLine = U3125ForfaitWizardPreviewLineOk | U3125ForfaitWizardPreviewLineError;

/** Ligne preview + état édition étape 2. */
export type U3125ForfaitWizardEditableLine = U3125ForfaitWizardPreviewLineOk & {
  invoiceTypeCode: string;
  invoiceAmount: number;
  taxRateCode?: string | null;
  included: boolean;
};

export type U3125ForfaitWizardCommitRequest = {
  invoiceDate: string;
  releaseAfterCreate: boolean;
  lines: Array<{
    projectId: string;
    invoiceTypeCode: "FP" | "FF";
    invoiceAmount: number;
    taxRateCode?: string | null;
    remark?: string | null;
    excluded: boolean;
  }>;
};

export type U3125ForfaitWizardCommitLineResult = {
  projectId?: string;
  projetNumber?: string;
  status: "created" | "skipped" | "error";
  invoiceId?: string;
  invoiceStatus?: "OPEN" | "RELEASE";
  invoiceNumber?: string | null;
  errorCode?: string;
  errorMessage?: string;
};

export function u3125ForfaitWizardPreviewLineIsOk(line: U3125ForfaitWizardPreviewLine): line is U3125ForfaitWizardPreviewLineOk {
  return line.status === "ok";
}

/** Montant facture nul — affichage aligné projet horaire (non facturable dans le wizard). */
export function u3125ForfaitWizardLineIsBillingComplete(
  preview: U3125ForfaitWizardPreviewLine,
  edited?: Pick<U3125ForfaitWizardEditableLine, "invoiceAmount"> | null,
): boolean {
  if (!u3125ForfaitWizardPreviewLineIsOk(preview)) return false;
  const amount =
    edited != null
      ? Number(edited.invoiceAmount) || 0
      : Number(preview.suggestedInvoiceAmount ?? preview.remainingBudget ?? 0);
  return amount <= 0.005;
}

export function u3125ForfaitWizardTodayIsoDate(): string {
  return u2000TodayOrgYmd();
}

/** FF si montant facture ≥ restant à facturer, sinon FP (tolérance centimes, aligné u3125SuggestInvoiceTypeCodeFromAmounts). */
export function u3125ForfaitWizardSuggestInvoiceTypeCode(invoiceAmount: number, remainingBudget: number): "FP" | "FF" {
  const amount = Number(invoiceAmount) || 0;
  const remaining = Math.max(0, Number(remainingBudget) || 0);
  if (amount >= remaining - 0.005) return "FF";
  return "FP";
}

export function u3125MapPreviewToEditableLines(lines: U3125ForfaitWizardPreviewLine[]): U3125ForfaitWizardEditableLine[] {
  return lines.filter(u3125ForfaitWizardPreviewLineIsOk).map((line) => {
    const invoiceAmount = Number(line.suggestedInvoiceAmount ?? line.remainingBudget ?? 0);
    return {
      ...line,
      invoiceTypeCode: u3125ForfaitWizardSuggestInvoiceTypeCode(invoiceAmount, line.remainingBudget ?? 0),
      invoiceAmount,
      taxRateCode: line.suggestedTaxRateCode ?? null,
      included: invoiceAmount > 0.005,
    };
  });
}

export function u3125BuildForfaitWizardCommitBody(params: {
  invoiceDate: string;
  releaseAfterCreate: boolean;
  previewLines: U3125ForfaitWizardPreviewLine[];
  editableLines: U3125ForfaitWizardEditableLine[];
}): U3125ForfaitWizardCommitRequest {
  const okByProjectId = new Map(params.editableLines.map((l) => [l.projectId, l]));
  const lines: U3125ForfaitWizardCommitRequest["lines"] = [];

  for (const preview of params.previewLines) {
    if (!u3125ForfaitWizardPreviewLineIsOk(preview)) {
      continue;
    }
    const edited = okByProjectId.get(preview.projectId);
    if (!edited) continue;
    const typeCode = edited.invoiceTypeCode?.trim().toUpperCase() === "FF" ? "FF" : "FP";
    let taxRateCode: string | null | undefined = edited.taxRateCode ?? preview.suggestedTaxRateCode ?? null;
    if (preview.customer?.taxable === false) {
      taxRateCode = null;
    }
    lines.push({
      projectId: preview.projectId,
      invoiceTypeCode: typeCode,
      invoiceAmount: Number(edited.invoiceAmount) || 0,
      taxRateCode: taxRateCode ?? undefined,
      remark: null,
      excluded: !edited.included,
    });
  }

  return {
    invoiceDate: U2000ApiValueToDisplayYmd(params.invoiceDate) ?? params.invoiceDate.trim().slice(0, 10),
    releaseAfterCreate: params.releaseAfterCreate,
    lines,
  };
}

export function u3125ForfaitWizardStatusBadgeClass(status: U3125ForfaitWizardPreviewLineStatus): string {
  if (status === "ok") return "bg-green-100 text-green-800 border-green-200";
  if (status === "billing_complete") return "bg-slate-100 text-slate-700 border-slate-300";
  return "bg-amber-100 text-amber-900 border-amber-200";
}

export function parseForfaitWizardPreviewLines(data: unknown): U3125ForfaitWizardPreviewLine[] {
  const root = data as { result?: { lines?: U3125ForfaitWizardPreviewLine[] }; lines?: U3125ForfaitWizardPreviewLine[] };
  const lines = root?.result?.lines ?? root?.lines;
  return Array.isArray(lines) ? lines : [];
}

export function parseForfaitWizardCommitLines(data: unknown): U3125ForfaitWizardCommitLineResult[] {
  const root = data as { result?: { lines?: U3125ForfaitWizardCommitLineResult[] }; lines?: U3125ForfaitWizardCommitLineResult[] };
  const lines = root?.result?.lines ?? root?.lines;
  return Array.isArray(lines) ? lines : [];
}

/** Corps commun rapport CAR et exports async (BL-0028). */
export interface U3125ArAgingRequest {
  asOfDate: string;
  lang?: "fr" | "en";
  /** Tranche d'âge active (filtre UI) — optionnel, rapport complet si absent. */
  bucket?: U2000AgingBucket;
  /** Projet filtré (filtre UI grille) — optionnel. */
  projectId?: string;
}

/** Construit le corps API CAR ; n'inclut bucket/projectId que si définis. */
export function u3125BuildArAgingRequestBody(params: {
  asOfDate: string;
  lang?: "fr" | "en";
  bucket?: U2000AgingBucket | null;
  projectId?: string | null;
}): U3125ArAgingRequest {
  const body: U3125ArAgingRequest = {
    asOfDate: params.asOfDate.trim().slice(0, 10),
  };
  if (params.lang) body.lang = params.lang;
  if (params.bucket) body.bucket = params.bucket;
  if (params.projectId?.trim()) body.projectId = params.projectId.trim();
  return body;
}

export interface U3125ArAgingBucketSummary {
  bucket: U2000AgingBucket | string;
  invoiceCount: number;
  balanceDue: number;
}

export interface U3125ArAgingTotals {
  invoiceCount: number;
  balanceDue: number;
}

export interface U3125ArAgingCustomerRef {
  id: string;
  customerCode?: string;
  customerName?: string;
}

export interface U3125ArAgingProjectRef {
  id: string;
  projetNumber?: string;
  projetName?: string;
}

export interface U3125ArAgingOrganization {
  buckets: U3125ArAgingBucketSummary[];
  totals: U3125ArAgingTotals;
}

export interface U3125ArAgingProjectRow {
  project: U3125ArAgingProjectRef;
  buckets: U3125ArAgingBucketSummary[];
  totals: U3125ArAgingTotals;
}

/** Ligne client du sommaire CAR (sans projects). */
export interface U3125ArAgingSummaryCustomerRow {
  customer: U3125ArAgingCustomerRef;
  buckets: U3125ArAgingBucketSummary[];
  totals: U3125ArAgingTotals;
}

export interface U3125ArAgingSummaryReport {
  asOfDate: string;
  agingBasis: string;
  organization: U3125ArAgingOrganization;
  customers: U3125ArAgingSummaryCustomerRow[];
}

export interface U3125ArAgingCustomerDetailRequest {
  asOfDate: string;
  customerId: string;
  lang?: "fr" | "en";
  bucket?: U2000AgingBucket;
}

export interface U3125ArAgingCustomerDetailReport {
  customer: U3125ArAgingCustomerRef;
  buckets: U3125ArAgingBucketSummary[];
  totals: U3125ArAgingTotals;
  projects: U3125ArAgingProjectRow[];
  invoices: U3125ArAgingInvoiceLine[];
}

export interface U3125ArAgingCustomerRow {
  customer: U3125ArAgingCustomerRef;
  buckets: U3125ArAgingBucketSummary[];
  totals: U3125ArAgingTotals;
  projects: U3125ArAgingProjectRow[];
}

export interface U3125ArAgingInvoiceLine {
  invoiceId: string;
  invoiceNumber?: string | number | null;
  invoiceDate?: string | null;
  balanceDue: number;
  bucket: U2000AgingBucket | string;
  remark?: string | null;
  customerId: string;
  projectId: string;
}

export interface U3125ArAgingReport {
  asOfDate: string;
  agingBasis: string;
  organization: U3125ArAgingOrganization;
  customers: U3125ArAgingCustomerRow[];
  invoices: U3125ArAgingInvoiceLine[];
}

export function u3125ArAgingBucketMap(buckets: U3125ArAgingBucketSummary[]): Map<string, U3125ArAgingBucketSummary> {
  const map = new Map<string, U3125ArAgingBucketSummary>();
  for (const b of buckets ?? []) {
    if (b?.bucket != null) map.set(String(b.bucket), b);
  }
  for (const code of U2000_AGING_BUCKETS) {
    if (!map.has(code)) {
      map.set(code, { bucket: code, invoiceCount: 0, balanceDue: 0 });
    }
  }
  return map;
}

export function u3125ArAgingEmptyTotals(): U3125ArAgingTotals {
  return { invoiceCount: 0, balanceDue: 0 };
}

function parseArAgingBucketSummary(raw: unknown): U3125ArAgingBucketSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const bucket = o.bucket;
  if (bucket == null || bucket === "") return null;
  return {
    bucket: String(bucket),
    invoiceCount: Number(o.invoiceCount) || 0,
    balanceDue: Number(o.balanceDue) || 0,
  };
}

function parseArAgingTotals(raw: unknown): U3125ArAgingTotals {
  if (!raw || typeof raw !== "object") return u3125ArAgingEmptyTotals();
  const o = raw as Record<string, unknown>;
  return {
    invoiceCount: Number(o.invoiceCount) || 0,
    balanceDue: Number(o.balanceDue) || 0,
  };
}

function parseArAgingOrganization(raw: unknown): U3125ArAgingOrganization {
  if (!raw || typeof raw !== "object") {
    return { buckets: [], totals: u3125ArAgingEmptyTotals() };
  }
  const o = raw as Record<string, unknown>;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseArAgingBucketSummary).filter((b): b is U3125ArAgingBucketSummary => b != null)
    : [];
  return { buckets, totals: parseArAgingTotals(o.totals) };
}

function parseArAgingProjectRow(raw: unknown): U3125ArAgingProjectRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const projectRaw = o.project;
  if (!projectRaw || typeof projectRaw !== "object") return null;
  const p = projectRaw as Record<string, unknown>;
  const id = p.id != null ? String(p.id) : "";
  if (!id) return null;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseArAgingBucketSummary).filter((b): b is U3125ArAgingBucketSummary => b != null)
    : [];
  return {
    project: {
      id,
      projetNumber: p.projetNumber != null ? String(p.projetNumber) : undefined,
      projetName: p.projetName != null ? String(p.projetName) : undefined,
    },
    buckets,
    totals: parseArAgingTotals(o.totals),
  };
}

function parseArAgingCustomerRow(raw: unknown): U3125ArAgingCustomerRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customerRaw = o.customer;
  if (!customerRaw || typeof customerRaw !== "object") return null;
  const c = customerRaw as Record<string, unknown>;
  const id = c.id != null ? String(c.id) : "";
  if (!id) return null;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseArAgingBucketSummary).filter((b): b is U3125ArAgingBucketSummary => b != null)
    : [];
  const projects = Array.isArray(o.projects)
    ? o.projects.map(parseArAgingProjectRow).filter((p): p is U3125ArAgingProjectRow => p != null)
    : [];
  return {
    customer: {
      id,
      customerCode: c.customerCode != null ? String(c.customerCode) : undefined,
      customerName: c.customerName != null ? String(c.customerName) : undefined,
    },
    buckets,
    totals: parseArAgingTotals(o.totals),
    projects,
  };
}

function parseArAgingInvoiceLine(raw: unknown): U3125ArAgingInvoiceLine | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const invoiceId = o.invoiceId != null ? String(o.invoiceId) : "";
  const customerId = o.customerId != null ? String(o.customerId) : "";
  const projectId = o.projectId != null ? String(o.projectId) : "";
  if (!invoiceId) return null;
  return {
    invoiceId,
    invoiceNumber: o.invoiceNumber as string | number | null | undefined,
    invoiceDate: o.invoiceDate != null ? String(o.invoiceDate) : null,
    balanceDue: Number(o.balanceDue) || 0,
    bucket: o.bucket != null ? String(o.bucket) : "",
    remark: o.remark != null ? String(o.remark) : null,
    customerId,
    projectId,
  };
}

function parseArAgingSummaryCustomerRow(raw: unknown): U3125ArAgingSummaryCustomerRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customerRaw = o.customer;
  if (!customerRaw || typeof customerRaw !== "object") return null;
  const c = customerRaw as Record<string, unknown>;
  const id = c.id != null ? String(c.id) : "";
  if (!id) return null;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseArAgingBucketSummary).filter((b): b is U3125ArAgingBucketSummary => b != null)
    : [];
  return {
    customer: {
      id,
      customerCode: c.customerCode != null ? String(c.customerCode) : undefined,
      customerName: c.customerName != null ? String(c.customerName) : undefined,
    },
    buckets,
    totals: parseArAgingTotals(o.totals),
  };
}

export function parseU3125ArAgingSummaryResponse(data: unknown): U3125ArAgingSummaryReport | null {
  const root = data as { result?: unknown };
  const raw = root?.result ?? data;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customers = Array.isArray(o.customers)
    ? o.customers.map(parseArAgingSummaryCustomerRow).filter((c): c is U3125ArAgingSummaryCustomerRow => c != null)
    : [];
  return {
    asOfDate: o.asOfDate != null ? String(o.asOfDate).slice(0, 10) : "",
    agingBasis: o.agingBasis != null ? String(o.agingBasis) : "INVOICE_DATE",
    organization: parseArAgingOrganization(o.organization),
    customers,
  };
}

export function parseU3125ArAgingCustomerDetailResponse(data: unknown): U3125ArAgingCustomerDetailReport | null {
  const root = data as { result?: unknown };
  const raw = root?.result ?? data;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customerRaw = o.customer;
  if (!customerRaw || typeof customerRaw !== "object") return null;
  const c = customerRaw as Record<string, unknown>;
  const id = c.id != null ? String(c.id) : "";
  if (!id) return null;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseArAgingBucketSummary).filter((b): b is U3125ArAgingBucketSummary => b != null)
    : [];
  const projects = Array.isArray(o.projects)
    ? o.projects.map(parseArAgingProjectRow).filter((p): p is U3125ArAgingProjectRow => p != null)
    : [];
  const invoices = Array.isArray(o.invoices)
    ? o.invoices.map(parseArAgingInvoiceLine).filter((i): i is U3125ArAgingInvoiceLine => i != null)
    : [];
  return {
    customer: {
      id,
      customerCode: c.customerCode != null ? String(c.customerCode) : undefined,
      customerName: c.customerName != null ? String(c.customerName) : undefined,
    },
    buckets,
    totals: parseArAgingTotals(o.totals),
    projects,
    invoices,
  };
}

export function u3125ArAgingCacheKey(asOfDate: string, bucket?: U2000AgingBucket | null, customerId?: string): string {
  const date = asOfDate.trim().slice(0, 10);
  const b = bucket ?? "";
  const c = customerId ?? "";
  return `${date}|${b}|${c}`;
}

/** Projection rapport complet → sommaire (fallback si endpoint summary absent). */
export function u3125ReportToSummary(report: U3125ArAgingReport, bucket?: U2000AgingBucket | null): U3125ArAgingSummaryReport {
  if (!bucket) {
    return {
      asOfDate: report.asOfDate,
      agingBasis: report.agingBasis,
      organization: report.organization,
      customers: report.customers.map(({ customer, buckets, totals }) => ({ customer, buckets, totals })),
    };
  }
  const { customers } = u3125FilterArAgingReportByBucket(report, bucket);
  return {
    asOfDate: report.asOfDate,
    agingBasis: report.agingBasis,
    organization: {
      buckets: report.organization.buckets,
      totals: u3125ArAgingTotalsForBucket(report.organization.buckets, bucket),
    },
    customers: customers.map(({ customer, buckets: b, totals }) => ({ customer, buckets: b, totals })),
  };
}

/** Extrait le détail d'un client depuis le rapport complet (fallback). */
export function u3125ExtractCustomerDetailFromReport(
  report: U3125ArAgingReport,
  customerId: string,
  bucket?: U2000AgingBucket | null,
): U3125ArAgingCustomerDetailReport | null {
  const row = report.customers.find((c) => c.customer.id === customerId);
  if (!row) return null;
  let invoices = report.invoices.filter((i) => i.customerId === customerId);
  if (bucket) invoices = invoices.filter((i) => i.bucket === bucket);
  const projectIds = new Set(invoices.map((i) => i.projectId).filter(Boolean));
  const projects = row.projects.filter((p) => projectIds.has(p.project.id));
  return {
    customer: row.customer,
    buckets: row.buckets,
    totals: bucket ? u3125ArAgingTotalsForBucket(row.buckets, bucket) : row.totals,
    projects,
    invoices,
  };
}

export function u3125BuildArAgingCustomerDetailRequestBody(params: {
  asOfDate: string;
  customerId: string;
  lang?: "fr" | "en";
  bucket?: U2000AgingBucket | null;
}): U3125ArAgingCustomerDetailRequest {
  const body: U3125ArAgingCustomerDetailRequest = {
    asOfDate: params.asOfDate.trim().slice(0, 10),
    customerId: params.customerId.trim(),
  };
  if (params.lang) body.lang = params.lang;
  if (params.bucket) body.bucket = params.bucket;
  return body;
}

export function parseU3125ArAgingReportResponse(data: unknown): U3125ArAgingReport | null {
  const root = data as { result?: unknown };
  const raw = root?.result ?? data;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customers = Array.isArray(o.customers)
    ? o.customers.map(parseArAgingCustomerRow).filter((c): c is U3125ArAgingCustomerRow => c != null)
    : [];
  const invoices = Array.isArray(o.invoices)
    ? o.invoices.map(parseArAgingInvoiceLine).filter((i): i is U3125ArAgingInvoiceLine => i != null)
    : [];
  return {
    asOfDate: o.asOfDate != null ? String(o.asOfDate).slice(0, 10) : "",
    agingBasis: o.agingBasis != null ? String(o.agingBasis) : "INVOICE_DATE",
    organization: parseArAgingOrganization(o.organization),
    customers,
    invoices,
  };
}

export function u3125BuildArAgingCustomerProjectLookupsFromDetail(
  detail: U3125ArAgingCustomerDetailReport,
): {
  customerById: Map<string, U3125ArAgingCustomerRef>;
  projectById: Map<string, U3125ArAgingProjectRef & { customerId: string }>;
} {
  const customerById = new Map<string, U3125ArAgingCustomerRef>();
  const projectById = new Map<string, U3125ArAgingProjectRef & { customerId: string }>();
  customerById.set(detail.customer.id, detail.customer);
  for (const pr of detail.projects ?? []) {
    projectById.set(pr.project.id, { ...pr.project, customerId: detail.customer.id });
  }
  return { customerById, projectById };
}

export function u3125BuildArAgingCustomerProjectLookups(customers: U3125ArAgingCustomerRow[]): {
  customerById: Map<string, U3125ArAgingCustomerRef>;
  projectById: Map<string, U3125ArAgingProjectRef & { customerId: string }>;
} {
  const customerById = new Map<string, U3125ArAgingCustomerRef>();
  const projectById = new Map<string, U3125ArAgingProjectRef & { customerId: string }>();
  for (const row of customers) {
    customerById.set(row.customer.id, row.customer);
    for (const pr of row.projects ?? []) {
      projectById.set(pr.project.id, { ...pr.project, customerId: row.customer.id });
    }
  }
  return { customerById, projectById };
}

export function u3125FormatArAgingCustomerLabel(customer: U3125ArAgingCustomerRef | undefined): string {
  if (!customer) return "—";
  const code = customer.customerCode?.trim();
  const name = customer.customerName?.trim();
  if (code && name) return `${code} — ${name}`;
  return code || name || customer.id;
}

export function u3125FormatArAgingProjectLabel(project: U3125ArAgingProjectRef | undefined): string {
  if (!project) return "—";
  const num = project.projetNumber?.trim();
  const name = project.projetName?.trim();
  if (num && name) return `${num} — ${name}`;
  return num || name || project.id;
}

/** Filtre la liste clients du sommaire CAR par tranche (sans rechargement API). */
export function u3125FilterArAgingSummaryByBucket(
  customers: U3125ArAgingSummaryCustomerRow[],
  bucket: U2000AgingBucket,
): U3125ArAgingSummaryCustomerRow[] {
  return customers.filter((row) => {
    const bucketSummary = u3125ArAgingBucketMap(row.buckets).get(bucket);
    return (bucketSummary?.invoiceCount ?? 0) > 0 || Math.abs(bucketSummary?.balanceDue ?? 0) > 0;
  });
}

/** Filtre le détail client CAR par tranche (factures + projets). */
export function u3125FilterArAgingCustomerDetailByBucket(
  detail: U3125ArAgingCustomerDetailReport,
  bucket: U2000AgingBucket,
): U3125ArAgingCustomerDetailReport {
  const invoices = detail.invoices.filter((inv) => inv.bucket === bucket);
  const projectIds = new Set(invoices.map((inv) => inv.projectId).filter(Boolean));
  return {
    ...detail,
    totals: u3125ArAgingTotalsForBucket(detail.buckets, bucket),
    projects: detail.projects.filter((pr) => projectIds.has(pr.project.id)),
    invoices,
  };
}

/** Filtre rapport CAR par tranche d'âge (clients, projets, factures). */
export function u3125FilterArAgingReportByBucket(
  report: U3125ArAgingReport,
  bucket: U2000AgingBucket,
): { invoices: U3125ArAgingInvoiceLine[]; customers: U3125ArAgingCustomerRow[] } {
  const invoices = report.invoices.filter((inv) => inv.bucket === bucket);
  const customerIds = new Set(invoices.map((inv) => inv.customerId).filter(Boolean));
  const projectIds = new Set(invoices.map((inv) => inv.projectId).filter(Boolean));

  const customers = report.customers
    .filter((row) => customerIds.has(row.customer.id))
    .map((row) => ({
      ...row,
      projects: row.projects.filter((pr) => projectIds.has(pr.project.id)),
    }));

  return { invoices, customers };
}

export function u3125ArAgingTotalsForBucket(
  buckets: U3125ArAgingBucketSummary[],
  bucket: string,
): U3125ArAgingTotals {
  const summary = u3125ArAgingBucketMap(buckets).get(bucket);
  return {
    invoiceCount: summary?.invoiceCount ?? 0,
    balanceDue: summary?.balanceDue ?? 0,
  };
}

export const doGetArAgingReport = (axios: AxiosInstance, body: U3125ArAgingRequest) => {
  return axios.post<{ result: U3125ArAgingReport }>(LogicalUnitEndpoints.AR_AGING_REPORT, body, u2000SkipBusyIndicatorConfig);
};

export const doGetArAgingSummary = (axios: AxiosInstance, body: U3125ArAgingRequest) => {
  return axios.post<{ result: U3125ArAgingSummaryReport }>(
    LogicalUnitEndpoints.AR_AGING_SUMMARY,
    body,
    u2000SkipBusyIndicatorConfig,
  );
};

export const doGetArAgingCustomerDetail = (axios: AxiosInstance, body: U3125ArAgingCustomerDetailRequest) => {
  return axios.post<{ result: U3125ArAgingCustomerDetailReport }>(
    LogicalUnitEndpoints.AR_AGING_CUSTOMER_DETAIL,
    body,
    u2000SkipBusyIndicatorConfig,
  );
};

export const doExportArAgingExcel = (axios: AxiosInstance, body: U3125ArAgingRequest) => {
  return axios.post(LogicalUnitEndpoints.AR_AGING_EXPORT_EXCEL, body);
};

export const doExportArAgingPdf = (axios: AxiosInstance, body: U3125ArAgingRequest) => {
  return axios.post(LogicalUnitEndpoints.AR_AGING_EXPORT_PDF, body);
};

export const doForfaitWizardPreview = (axios: AxiosInstance, body: U3125ForfaitWizardPreviewRequest) => {
  return axios.post(LogicalUnitEndpoints.FORFAIT_WIZARD_PREVIEW, body);
};

export const doForfaitWizardCommit = (axios: AxiosInstance, body: U3125ForfaitWizardCommitRequest) => {
  return axios.post(LogicalUnitEndpoints.FORFAIT_WIZARD_COMMIT, body);
};

export function buildDeleteBody(row: U3125InvoiceFormRow): { id: string; version: number } {
  return { id: row.id, version: row.version };
}

/** Corps `POST /U3125/status/:id` — aligné `U3125InvoiceSetStatusSchema` (id URL = body.id). */
export interface U3125InvoiceSetStatusBody {
  id: string;
  version: number;
  status: U3125InvoiceStatus;
}

export function buildInvoiceSetStatusBody(row: U3125InvoiceFormRow, status: U3125InvoiceStatus): U3125InvoiceSetStatusBody {
  return { id: row.id, version: row.version, status };
}

/** Corps `POST /U3125/void/:id` — aligné `U3125InvoiceVoidSchema` (id URL = body.id). */
export interface U3125InvoiceVoidBody {
  id: string;
  version: number;
}

export function buildInvoiceVoidBody(row: Pick<U3125InvoiceFormRow, "id" | "version">): U3125InvoiceVoidBody {
  return { id: row.id, version: row.version };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang });
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doGetPdf = (axios: AxiosInstance, id: string | number) => {
  return axios.get(LogicalUnitEndpoints.PDF(id), {
    params: { b: U2000GenerateUrlUUID() },
    responseType: "blob",
  });
};

export const doGetCustomerCreditBalance = (axios: AxiosInstance, customerId: string) => {
  return axios.get(LogicalUnitEndpoints.CUSTOMER_CREDIT_BALANCE(customerId), { params: { b: U2000GenerateUrlUUID() } });
};

export function u3125NonCreditApplicationLines(lines: U3125InvoiceLineItem[]): U3125InvoiceLineItem[] {
  return lines.filter((l) => (l.kind as string) !== "CREDIT_APPLICATION");
}

export function u3125FindCreditApplicationLine(lines: U3125InvoiceLineItem[]): U3125InvoiceLineItem | undefined {
  return lines.find((l) => (l.kind as string) === "CREDIT_APPLICATION");
}

export function u3125FormatCreditSourceLabel(source: U3125CreditSource, t: TranslateFn, ns: string): string {
  const num = u3125FormatInvoiceNumberDisplay(source.invoiceNumber);
  const natureKey = source.nature === "ADVANCE" ? "lblDocumentNatureAdvance" : "lblDocumentNatureCredit";
  const natureLabel = t(natureKey, { ns, defaultValue: source.nature });
  const remaining = formatCurrency(source.remainingAmount, {
    currencySymbol: "$",
    symbolPosition: "left",
    decimalSeparator: ".",
    thousandSeparator: " ",
    decimalDigits: 2,
    allowNegative: true,
  });
  return t("lblCreditSourceOption", { ns, num, nature: natureLabel, remaining, defaultValue: "{{num}} — {{nature}} ({{remaining}})" });
}

export function u3125FormatCreditApplicationNature(nature: string | undefined, t: TranslateFn, ns: string): string {
  if (nature === "ADVANCE") return t("lblDocumentNatureAdvance", { ns });
  if (nature === "CREDIT") return t("lblDocumentNatureCredit", { ns });
  return nature ?? "";
}

/** Ligne historique sans ventilation composante (BL-0041). */
export function u3125IsLegacyCreditApplication(
  row: Pick<U3125InvoiceCreditApplicationItem, "amount" | "amountTaxable" | "amountNonTaxable">,
): boolean {
  const amount = Math.abs(Number(row.amount) || 0);
  const taxable = Math.abs(Number(row.amountTaxable) || 0);
  const nonTaxable = Math.abs(Number(row.amountNonTaxable) || 0);
  return amount > 0.005 && taxable + nonTaxable <= 0.005;
}

function u3125SumOtherCreditApplicationsOnInvoice(
  applications: readonly U3125InvoiceCreditApplicationItem[],
  availableSources: U3125CreditSource[] | undefined,
  editingRowId: string | undefined,
): { appliedTaxable: number; appliedNonTaxable: number; legacyOnTaxable: number } {
  let appliedTaxable = 0;
  let appliedNonTaxable = 0;
  let legacyOnTaxable = 0;
  for (const app of applications) {
    if (editingRowId && app.id === editingRowId) continue;
    const nature = u3125ResolveCreditApplicationNature(app, availableSources);
    if (nature !== "CREDIT") continue;
    const amount = Math.abs(Number(app.amount) || 0);
    if (amount <= 0) continue;
    if (u3125IsLegacyCreditApplication(app)) {
      legacyOnTaxable = u3125RoundTaxAmount(legacyOnTaxable + amount);
    } else {
      appliedTaxable = u3125RoundTaxAmount(appliedTaxable + Math.abs(Number(app.amountTaxable) || 0));
      appliedNonTaxable = u3125RoundTaxAmount(appliedNonTaxable + Math.abs(Number(app.amountNonTaxable) || 0));
    }
  }
  return { appliedTaxable, appliedNonTaxable, legacyOnTaxable };
}

/** Plafond imposable : min(solde source, brut facture restant) + baseline édition. */
export function u3125MaxCreditApplicationTaxableForRow(
  source: U3125CreditSource | undefined,
  baselineTaxable: number,
  invoice: Pick<U3125InvoiceFormRow, "amountInvoiceTaxable" | "amountInvoiceNonTaxable" | "creditApplications" | "availableCreditSources"> | null,
  editingRowId?: string,
): number {
  if (!source || source.nature !== "CREDIT") return 0;
  const maxFromSource = u3125RoundTaxAmount((source.remainingTaxable ?? 0) + Math.abs(Number(baselineTaxable) || 0));
  const grossTaxable = Math.abs(Number(invoice?.amountInvoiceTaxable ?? 0));
  const { appliedTaxable, legacyOnTaxable } = u3125SumOtherCreditApplicationsOnInvoice(
    invoice?.creditApplications ?? [],
    invoice?.availableCreditSources,
    editingRowId,
  );
  const maxFromTarget = Math.max(0, u3125RoundTaxAmount(grossTaxable - appliedTaxable - legacyOnTaxable));
  return Math.min(maxFromSource, maxFromTarget);
}

/** Plafond non imposable : min(solde source, brut facture restant) + baseline édition. */
export function u3125MaxCreditApplicationNonTaxableForRow(
  source: U3125CreditSource | undefined,
  baselineNonTaxable: number,
  invoice: Pick<U3125InvoiceFormRow, "amountInvoiceTaxable" | "amountInvoiceNonTaxable" | "creditApplications" | "availableCreditSources"> | null,
  editingRowId?: string,
): number {
  if (!source || source.nature !== "CREDIT") return 0;
  const maxFromSource = u3125RoundTaxAmount((source.remainingNonTaxable ?? 0) + Math.abs(Number(baselineNonTaxable) || 0));
  const grossNonTaxable = Math.abs(Number(invoice?.amountInvoiceNonTaxable ?? 0));
  const { appliedNonTaxable } = u3125SumOtherCreditApplicationsOnInvoice(
    invoice?.creditApplications ?? [],
    invoice?.availableCreditSources,
    editingRowId,
  );
  const maxFromTarget = Math.max(0, u3125RoundTaxAmount(grossNonTaxable - appliedNonTaxable));
  return Math.min(maxFromSource, maxFromTarget);
}

/** Plafond applicable : solde source + montant déjà enregistré sur la ligne en édition (baseline fixe à l'ouverture du dialogue). */
export function u3125MaxCreditApplicationAmountForRow(source: U3125CreditSource | undefined, baselineRowAmount: number): number {
  if (!source) return 0;
  return u3125RoundTaxAmount(source.remainingAmount + Math.abs(Number(baselineRowAmount) || 0));
}

export function u3125CanAddCreditApplication(
  availableSources: U3125CreditSource[],
  applications: U3125InvoiceCreditApplicationItem[],
): boolean {
  const usedIds = new Set(applications.map((app) => app.sourceInvoiceId.trim()).filter(Boolean));
  return availableSources.some((source) => source.remainingAmount > 0.005 && !usedIds.has(source.invoiceId));
}

export function u3125SelectableCreditSourcesForDialog(
  availableSources: U3125CreditSource[],
  applications: U3125InvoiceCreditApplicationItem[],
  editingSourceInvoiceId?: string,
): U3125CreditSource[] {
  const editingId = editingSourceInvoiceId?.trim() ?? "";
  const usedIds = new Set(
    applications
      .map((app) => app.sourceInvoiceId.trim())
      .filter((id) => id && id !== editingId),
  );
  return availableSources.filter((source) => source.remainingAmount > 0.005 && !usedIds.has(source.invoiceId));
}

export function u3125MergeCreditSourcesForDisplay(
  availableSources: U3125CreditSource[],
  applications: U3125InvoiceCreditApplicationItem[],
): U3125CreditSource[] {
  const byId = new Map(availableSources.map((source) => [source.invoiceId, source]));
  for (const app of applications) {
    const sourceId = app.sourceInvoiceId?.trim();
    if (!sourceId || byId.has(sourceId)) continue;
    byId.set(sourceId, {
      invoiceId: sourceId,
      invoiceNumber:
        app.sourceInvoiceNumber != null && String(app.sourceInvoiceNumber).trim() !== ""
          ? String(app.sourceInvoiceNumber).trim()
          : null,
      invoiceDate: app.sourceInvoiceDate ?? "",
      invoiceTypeCode: app.sourceInvoiceTypeCode ?? "",
      nature: app.sourceNature ?? "CREDIT",
      originalAmount: 0,
      appliedAmount: 0,
      remainingAmount: Math.abs(Number(app.amount) || 0),
      projectId: "",
      projetNumber: null,
      projetName: null,
    });
  }
  return [...byId.values()];
}

export function u3125ValidateCreditApplicationForm(
  data: U3125InvoiceCreditApplicationItem,
  _source: U3125CreditSource | undefined,
  limits:
    | { mode: "ADVANCE"; maxAmount: number }
    | { mode: "CREDIT"; maxTaxable: number; maxNonTaxable: number },
  t: TranslateFn,
  ns = "u3125",
): Record<string, string> {
  const errors: Record<string, string> = {};
  const sourceId = data.sourceInvoiceId?.trim() ?? "";
  if (!sourceId) {
    errors.sourceInvoiceId = t("errCreditApplicationSourceRequired", { ns });
    return errors;
  }

  if (limits.mode === "ADVANCE") {
    const amount = Math.abs(Number(data.amount) || 0);
    if (amount <= 0) {
      errors.amount = t("errCreditApplicationAmountRequired", { ns });
    } else if (amount > limits.maxAmount + 0.005) {
      errors.amount = t("errCreditApplicationExceedsRemaining", {
        ns,
        max: formatCurrency(limits.maxAmount, {
          currencySymbol: "$",
          symbolPosition: "left",
          decimalSeparator: ".",
          thousandSeparator: " ",
          decimalDigits: 2,
          allowNegative: true,
        }),
      });
    }
    return errors;
  }

  const amountTaxable = Math.abs(Number(data.amountTaxable) || 0);
  const amountNonTaxable = Math.abs(Number(data.amountNonTaxable) || 0);
  const total = u3125RoundTaxAmount(amountTaxable + amountNonTaxable);
  if (total <= 0) {
    errors.amountTaxable = t("errCreditApplicationSplitRequired", { ns });
    return errors;
  }
  if (amountTaxable > limits.maxTaxable + 0.005) {
    errors.amountTaxable = t("errCreditApplicationExceedsMaxTaxable", {
      ns,
      max: formatCurrency(limits.maxTaxable, {
        currencySymbol: "$",
        symbolPosition: "left",
        decimalSeparator: ".",
        thousandSeparator: " ",
        decimalDigits: 2,
        allowNegative: false,
      }),
    });
  }
  if (amountNonTaxable > limits.maxNonTaxable + 0.005) {
    errors.amountNonTaxable = t("errCreditApplicationExceedsMaxNonTaxable", {
      ns,
      max: formatCurrency(limits.maxNonTaxable, {
        currencySymbol: "$",
        symbolPosition: "left",
        decimalSeparator: ".",
        thousandSeparator: " ",
        decimalDigits: 2,
        allowNegative: false,
      }),
    });
  }
  return errors;
}

export function u3125ValidateCreditApplications(row: U3125InvoiceFormRow, t: TranslateFn, ns = "u3125"): Record<string, string> {
  if (row.documentNature !== "STANDARD") return {};
  const seen = new Set<string>();
  for (const app of row.creditApplications ?? []) {
    const sourceId = app.sourceInvoiceId?.trim();
    if (!sourceId) continue;
    if (seen.has(sourceId)) {
      return { creditApplications: t("errCreditApplicationDuplicateSource", { ns }) };
    }
    seen.add(sourceId);
  }
  return {};
}

/** IDs d'onglets formulaire à marquer en erreur selon les clés de validation. */
export function u3125ResolveTabsWithValidationErrors(errors: Record<string, string>): string[] {
  const keys = Object.keys(errors);
  if (keys.length === 0) return [];
  const tabs: string[] = [];
  const has = (pred: (key: string) => boolean) => keys.some(pred);

  if (has((k) => k === "creditApplications" || k.startsWith("creditApplication"))) {
    tabs.push("tabCreditApplications");
  }
  if (has((k) => k === "lines" || k.startsWith("lines.") || k.startsWith("line."))) {
    tabs.push("tabInvoiceLines");
  }
  if (has((k) => k === "payments" || k.startsWith("payments.") || k.startsWith("payment."))) {
    tabs.push("tabPayments");
  }
  if (has((k) => k.includes("wip") || k.startsWith("workInProgress"))) {
    tabs.push("tabWipLines");
  }
  return tabs;
}

export function parseCustomerCreditBalanceFromApi(data: unknown): U3125CustomerCreditBalance | null {
  if (!data || typeof data !== "object") return null;
  const wrapper = data as { result?: U3125CustomerCreditBalance };
  const payload = wrapper.result ?? (data as U3125CustomerCreditBalance);
  if (!payload?.customerId) return null;
  return {
    customerId: payload.customerId,
    totalBalance: Number(payload.totalBalance) || 0,
    availableSources: (payload.availableSources ?? []).map((s) => ({
      ...s,
      invoiceNumber:
        s.invoiceNumber != null && String(s.invoiceNumber).trim() !== "" ? String(s.invoiceNumber).trim() : null,
      remainingAmount: Number(s.remainingAmount) || 0,
      originalAmount: Number(s.originalAmount) || 0,
      appliedAmount: Number(s.appliedAmount) || 0,
    })),
  };
}

/** Totaux affichés à partir des montants d’en-tête (crédits déjà intégrés côté backend après save). */
export function u3125InvoiceDisplayTotals(
  row: U3125InvoiceFormRow,
  _taxRates: U3125TaxRateInitItem[],
): { subtotal: number; taxAmount1: number; taxAmount2: number; total: number } {
  const taxable = row.amountInvoiceTaxable ?? 0;
  const nonTaxable = row.amountInvoiceNonTaxable ?? 0;
  const taxAmount1 = row.taxAmount1 ?? 0;
  const taxAmount2 = row.taxAmount2 ?? 0;
  const subtotal = u3125RoundTaxAmount(taxable + nonTaxable);
  return {
    subtotal,
    taxAmount1,
    taxAmount2,
    total: u3125RoundTaxAmount(subtotal + taxAmount1 + taxAmount2),
  };
}

function u3125ResolveCreditApplicationNature(
  app: U3125InvoiceCreditApplicationItem,
  availableSources: U3125CreditSource[] | undefined,
): U3125InvoiceTypeNature | undefined {
  if (app.sourceNature === "CREDIT" || app.sourceNature === "ADVANCE") return app.sourceNature;
  const sourceId = app.sourceInvoiceId?.trim();
  if (!sourceId) return undefined;
  return availableSources?.find((source) => source.invoiceId === sourceId)?.nature;
}

function u3125SplitCreditApplicationAmounts(
  applications: readonly U3125InvoiceCreditApplicationItem[],
  availableSources: U3125CreditSource[] | undefined,
): { creditApplicationsBeforeTaxTotal: number; advanceApplicationsTotal: number } {
  let creditApplicationsBeforeTaxTotal = 0;
  let advanceApplicationsTotal = 0;
  for (const app of applications) {
    const amount = Math.abs(Number(app.amount) || 0);
    if (amount <= 0) continue;
    const nature = u3125ResolveCreditApplicationNature(app, availableSources);
    if (nature === "CREDIT") creditApplicationsBeforeTaxTotal += amount;
    else if (nature === "ADVANCE") advanceApplicationsTotal += amount;
  }
  return {
    creditApplicationsBeforeTaxTotal: u3125RoundTaxAmount(creditApplicationsBeforeTaxTotal),
    advanceApplicationsTotal: u3125RoundTaxAmount(advanceApplicationsTotal),
  };
}

/** Somme des montants NC appliqués ventilés (CREDIT) vs legacy (montant total sur imposable). */
function u3125SumAppliedCreditSplits(
  creditApplications: readonly U3125InvoiceCreditApplicationItem[] | undefined,
  availableSources?: U3125CreditSource[],
): { creditTaxable: number; creditNonTaxable: number; legacyCreditBeforeTax: number } {
  let creditTaxable = 0;
  let creditNonTaxable = 0;
  let legacyCreditBeforeTax = 0;
  for (const app of creditApplications ?? []) {
    const nature = u3125ResolveCreditApplicationNature(app, availableSources);
    if (nature !== "CREDIT") continue;
    const amount = Math.abs(Number(app.amount) || 0);
    if (amount <= 0) continue;
    if (u3125IsLegacyCreditApplication(app)) {
      legacyCreditBeforeTax = u3125RoundTaxAmount(legacyCreditBeforeTax + amount);
      continue;
    }
    creditTaxable = u3125RoundTaxAmount(creditTaxable + Math.abs(Number(app.amountTaxable) || 0));
    creditNonTaxable = u3125RoundTaxAmount(creditNonTaxable + Math.abs(Number(app.amountNonTaxable) || 0));
  }
  return { creditTaxable, creditNonTaxable, legacyCreditBeforeTax };
}

/** Reconstruit les montants bruts d’en-tête à partir du net persisté (GET après save). */
export function u3125RestoreGrossAmountsFromNetHeader(
  row: Pick<U3125InvoiceFormRow, "amountInvoiceTaxable" | "amountInvoiceNonTaxable">,
  credits: { creditTaxable: number; creditNonTaxable: number; legacyCreditBeforeTax: number },
): { amountInvoiceTaxable: number; amountInvoiceNonTaxable: number } {
  const netTaxable = u3125RoundTaxAmount(Number(row.amountInvoiceTaxable ?? 0));
  const netNonTaxable = u3125RoundTaxAmount(Number(row.amountInvoiceNonTaxable ?? 0));
  const creditTaxable = u3125RoundTaxAmount(Math.abs(credits.creditTaxable ?? 0));
  const creditNonTaxable = u3125RoundTaxAmount(Math.abs(credits.creditNonTaxable ?? 0));
  const legacy = u3125RoundTaxAmount(Math.abs(credits.legacyCreditBeforeTax ?? 0));
  const totalCredits = u3125RoundTaxAmount(creditTaxable + creditNonTaxable + legacy);

  if (totalCredits <= 0.005) {
    return { amountInvoiceTaxable: netTaxable, amountInvoiceNonTaxable: netNonTaxable };
  }

  if (creditTaxable + creditNonTaxable > 0.005) {
    return {
      amountInvoiceTaxable: u3125RoundTaxAmount(netTaxable + creditTaxable + legacy),
      amountInvoiceNonTaxable: u3125RoundTaxAmount(netNonTaxable + creditNonTaxable),
    };
  }

  if (netTaxable > 0.005) {
    return {
      amountInvoiceTaxable: u3125RoundTaxAmount(netTaxable + legacy),
      amountInvoiceNonTaxable: netNonTaxable,
    };
  }
  const netSubtotal = u3125RoundTaxAmount(netTaxable + netNonTaxable);
  const grossSubtotal = u3125RoundTaxAmount(netSubtotal + legacy);
  return {
    amountInvoiceTaxable: 0,
    amountInvoiceNonTaxable: grossSubtotal,
  };
}

/**
 * Montants bruts pour le formulaire : l’API renvoie le net après notes de crédit sur facture STANDARD ;
 * les champs Montant taxable / non taxable du panneau Facture restent toujours au brut (les crédits n’affectent que le panneau Montants).
 */
export function u3125ResolveGrossHeaderAmountsFromInvoice(
  inv: Pick<
    U3125Invoice,
    "documentNature" | "amountInvoiceTaxable" | "amountInvoiceNonTaxable" | "billingTotals" | "creditApplications"
  >,
): { amountInvoiceTaxable: number; amountInvoiceNonTaxable: number } {
  const taxable = Number(inv.amountInvoiceTaxable ?? 0);
  const nonTaxable = Number(inv.amountInvoiceNonTaxable ?? 0);
  if ((inv.documentNature ?? "STANDARD") !== "STANDARD") {
    return {
      amountInvoiceTaxable: u3125RoundTaxAmount(taxable),
      amountInvoiceNonTaxable: u3125RoundTaxAmount(nonTaxable),
    };
  }
  const creditBeforeTax = inv.billingTotals?.creditApplicationsBeforeTaxTotal ?? 0;
  const splits = u3125SumAppliedCreditSplits(inv.creditApplications);
  const hasSplit = splits.creditTaxable + splits.creditNonTaxable > 0.005;
  const legacyCreditBeforeTax =
    splits.legacyCreditBeforeTax > 0.005 ? splits.legacyCreditBeforeTax : !hasSplit && creditBeforeTax > 0 ? creditBeforeTax : 0;

  if (!hasSplit && legacyCreditBeforeTax <= 0.005) {
    return {
      amountInvoiceTaxable: u3125RoundTaxAmount(taxable),
      amountInvoiceNonTaxable: u3125RoundTaxAmount(nonTaxable),
    };
  }

  return u3125RestoreGrossAmountsFromNetHeader(
    { amountInvoiceTaxable: taxable, amountInvoiceNonTaxable: nonTaxable },
    {
      creditTaxable: splits.creditTaxable,
      creditNonTaxable: splits.creditNonTaxable,
      legacyCreditBeforeTax,
    },
  );
}

/** Applique les notes de crédit sur la base imposable avant calcul des taxes (aligné backend BL-0041). */
export function u3125ApplyCreditApplicationsToGrossAmounts(
  gross: Pick<U3125InvoiceFormRow, "amountInvoiceTaxable" | "amountInvoiceNonTaxable">,
  creditApplicationsBeforeTaxTotal: number,
  row: Pick<U3125InvoiceFormRow, "customerTaxable" | "taxRateCode" | "documentNature" | "invoiceTypeCode">,
  taxRates: U3125TaxRateInitItem[],
  invoiceTypes?: readonly U3125InvoiceTypeInitItem[],
  creditApplications?: readonly U3125InvoiceCreditApplicationItem[],
  availableSources?: U3125CreditSource[],
): { subtotal: number; taxAmount1: number; taxAmount2: number } {
  const netHeader = u3125NetHeaderAmountsAfterCreditApplications(
    gross,
    creditApplicationsBeforeTaxTotal,
    creditApplications,
    availableSources,
  );
  const taxable = netHeader.amountInvoiceTaxable;
  const nonTaxable = netHeader.amountInvoiceNonTaxable;

  let taxAmount1 = 0;
  let taxAmount2 = 0;
  if (u3125InvoiceRequiresTaxRateCode(row, invoiceTypes) && row.taxRateCode?.trim()) {
    const rate = u3125FindTaxRateByCode(taxRates, row.taxRateCode);
    const taxes = u3125ComputeTaxAmountsFromTaxableBase(taxable, rate?.taxRateComponents ?? []);
    taxAmount1 = taxes.taxAmount1 ?? 0;
    taxAmount2 = taxes.taxAmount2 ?? 0;
  }

  return {
    subtotal: u3125RoundTaxAmount(taxable + nonTaxable),
    taxAmount1,
    taxAmount2,
  };
}

export type U3125MontantsPanelTotals = U3125InvoiceBillingTotals & {
  subtotal: number;
  taxAmount1: number;
  taxAmount2: number;
};

/** Totaux panneau Montants — preview locale incluant notes de crédit avant taxes. */
export function u3125ResolveMontantsPanelTotals(
  row: U3125InvoiceFormRow,
  taxRates: U3125TaxRateInitItem[],
  invoiceTypes?: readonly U3125InvoiceTypeInitItem[],
): U3125MontantsPanelTotals | null {
  if (row.documentNature !== "STANDARD") return null;

  const { creditApplicationsBeforeTaxTotal, advanceApplicationsTotal } = u3125SplitCreditApplicationAmounts(
    row.creditApplications ?? [],
    row.availableCreditSources,
  );

  let grossTaxable = Number(row.amountInvoiceTaxable ?? 0);
  let grossNonTaxable = Number(row.amountInvoiceNonTaxable ?? 0);

  const net = u3125ApplyCreditApplicationsToGrossAmounts(
    { amountInvoiceTaxable: grossTaxable, amountInvoiceNonTaxable: grossNonTaxable },
    creditApplicationsBeforeTaxTotal,
    row,
    taxRates,
    invoiceTypes,
    row.creditApplications ?? [],
    row.availableCreditSources,
  );

  const invoiceTotal = u3125RoundTaxAmount(net.subtotal + net.taxAmount1 + net.taxAmount2);
  const grandTotal = u3125RoundTaxAmount(Math.max(0, invoiceTotal - advanceApplicationsTotal));

  return {
    creditApplicationsBeforeTaxTotal,
    advanceApplicationsTotal,
    subtotal: net.subtotal,
    taxAmount1: net.taxAmount1,
    taxAmount2: net.taxAmount2,
    invoiceTotal,
    grandTotal,
  };
}

/** Totaux facture / acomptes / grand total — API après save, preview locale depuis `creditApplications`. */
export function u3125ResolveBillingTotalsForDisplay(
  row: U3125InvoiceFormRow,
  taxRates: U3125TaxRateInitItem[],
  invoiceTypes?: readonly U3125InvoiceTypeInitItem[],
): U3125InvoiceBillingTotals {
  const montants = u3125ResolveMontantsPanelTotals(row, taxRates, invoiceTypes);
  if (!montants) {
    const header = u3125InvoiceDisplayTotals(row, taxRates);
    return {
      creditApplicationsBeforeTaxTotal: 0,
      advanceApplicationsTotal: 0,
      invoiceTotal: header.total,
      grandTotal: header.total,
    };
  }
  return {
    creditApplicationsBeforeTaxTotal: montants.creditApplicationsBeforeTaxTotal,
    advanceApplicationsTotal: montants.advanceApplicationsTotal,
    invoiceTotal: montants.invoiceTotal,
    grandTotal: montants.grandTotal,
  };
}

function toOptionalNumber(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function toOptionalRate(value: unknown): number | null | undefined {
  if (value == null) return value === null ? null : undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** `true` = horaire (afficher vendant + coutant) ; `false` = forfaitaire (coutant seulement). */
export function isU3125WipLineHourly(row: Pick<U3134WorkInProgressSearchRow, "hourly">): boolean {
  return row.hourly !== false;
}

export type U3125WipTransactionKindFilter = Extract<U3134TransactionKind, "honorarium" | "expense" | "equipment">;

/** Filtre les lignes TEC par nature (aucun filtre actif = aucune ligne affichée). */
export function u3125FilterWipRowsByKind(
  rows: U3134WorkInProgressSearchRow[],
  activeFilters: ReadonlySet<U3125WipTransactionKindFilter>,
): U3134WorkInProgressSearchRow[] {
  if (activeFilters.size === 0) return [];
  return rows.filter((row) => {
    const kind = row.transactionKind;
    return kind != null && activeFilters.has(kind as U3125WipTransactionKindFilter);
  });
}

/** Libellé code + description pour type dépense / équipement. */
export function u3125FormatExpenseOrEquipmentType(ref?: U3134ExpenseTypeRef | null): string {
  if (!ref) return "";
  const code = ref.code?.trim() ?? "";
  const desc = ref.description?.trim() ?? "";
  if (code && desc) return `${code} — ${desc}`;
  return code || desc;
}

/** Libellé traduit de la nature TEC (honorarium, dépense, équipement). */
export function u3125FormatWipTransactionKindLabel(
  kind: string | null | undefined,
  t: TranslateFn,
  ns = "u3125",
): string {
  const k = kind?.trim().toLowerCase();
  if (!k) return "";
  return t(`lblTransactionKind_${k}`, { ns, defaultValue: k });
}

/** Colonne « Entrée / type » : entrée honoraires, ou type dépense / équipement selon la ligne. */
export function u3125FormatWipEntryColumn(
  row: Pick<U3134WorkInProgressSearchRow, "transactionKind" | "honorariumEntryKind" | "expenseType" | "equipmentType">,
  t?: TranslateFn,
  ns = "u3125",
): string {
  const transactionKind = String(row.transactionKind ?? "").toLowerCase();
  if (transactionKind === "expense" || row.expenseType) {
    const expenseLabel = u3125FormatExpenseOrEquipmentType(row.expenseType);
    if (expenseLabel) return expenseLabel;
  }
  if (transactionKind === "equipment" || row.equipmentType) {
    const equipmentLabel = u3125FormatExpenseOrEquipmentType(row.equipmentType);
    if (equipmentLabel) return equipmentLabel;
  }
  const entry = row.honorariumEntryKind?.trim();
  if (!entry) return "";
  if (t) {
    return t(`lblHonorariumEntryKind_${entry}`, { ns, defaultValue: entry });
  }
  return entry;
}

function parseU3125TransactionKind(raw: unknown): U3134WorkInProgressSearchRow["transactionKind"] | undefined {
  if (typeof raw === "string") {
    const k = raw.trim().toLowerCase();
    if (k === "honorarium" || k === "expense" || k === "equipment") return k;
    return undefined;
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const code = (raw as Record<string, unknown>).code;
    if (typeof code === "string") return parseU3125TransactionKind(code);
  }
  return undefined;
}

function parseU3125ExpenseTypeRef(raw: unknown): U3134ExpenseTypeRef | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  return {
    code: (o.code as string | null | undefined) ?? null,
    description: (o.description as string | null | undefined) ?? null,
  };
}

function parseU3125EquipmentTypeRef(raw: unknown): U3134ExpenseTypeRef | null {
  const base = parseU3125ExpenseTypeRef(raw);
  if (!base || !raw || typeof raw !== "object" || Array.isArray(raw)) return base;
  const unit = String((raw as Record<string, unknown>).billingUnit ?? (raw as Record<string, unknown>).billing_unit ?? "").trim();
  if (!unit) return base;
  const desc = base.description?.trim() ?? "";
  return { ...base, description: desc ? `${desc} (${unit})` : unit };
}

function parseU3125WipTaskRef(raw: unknown): U3134TaskRef | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? "").trim();
  if (!id) return null;
  const strOrUndef = (v: unknown): string | undefined => {
    if (v == null) return undefined;
    const s = String(v).trim();
    return s || undefined;
  };
  const strOrNull = (v: unknown): string | null | undefined => {
    if (v === null) return null;
    if (v === undefined) return undefined;
    const s = String(v).trim();
    return s || null;
  };
  return {
    id,
    taskCode: strOrUndef(o.taskCode),
    taskDescriptionFr: strOrNull(o.taskDescriptionFr),
    taskDescriptionEn: strOrNull(o.taskDescriptionEn),
  };
}

/** Normalise une ligne `GET /U3125/wipTransactions` (DTO U3134 GET) vers le modèle UI. */
export function normalizeU3125WipTransactionLine(raw: Record<string, unknown>): U3134WorkInProgressSearchRow {
  const emp = raw.employee;
  const employee =
    emp && typeof emp === "object" && !Array.isArray(emp)
      ? {
          id: String((emp as Record<string, unknown>).id ?? ""),
          codeEmployee: (emp as Record<string, unknown>).codeEmployee as string | undefined,
          numEmployee: (emp as Record<string, unknown>).numEmployee as string | undefined,
          firstName: (emp as Record<string, unknown>).firstName as string | undefined,
          lastName: (emp as Record<string, unknown>).lastName as string | undefined,
        }
      : null;

  const sellingRate = toOptionalRate(raw.sellingRate ?? raw.rate);
  const sellingMarkupFactor = toOptionalNumber(raw.sellingMarkupFactor ?? raw.markupFactor);
  const costRate = toOptionalRate(raw.costRate);
  const costMarkupFactor = toOptionalNumber(raw.costMarkupFactor);
  const invoiceRaw = raw.invoice;
  const invoice =
    invoiceRaw && typeof invoiceRaw === "object" && !Array.isArray(invoiceRaw)
      ? { id: String((invoiceRaw as Record<string, unknown>).id ?? "") || undefined }
      : invoiceRaw === null
        ? null
        : undefined;

  return {
    id: String(raw.id ?? ""),
    transactionKind: parseU3125TransactionKind(raw.transactionKind),
    honorariumEntryKind: (raw.honorariumEntryKind as U3134WorkInProgressSearchRow["honorariumEntryKind"]) ?? null,
    transactionDate: (raw.transactionDate as string | Date | undefined) ?? undefined,
    billable: raw.billable === true || raw.billable === false ? raw.billable : undefined,
    hourly: raw.hourly === true ? true : raw.hourly === false ? false : null,
    sellingRate,
    costRate,
    quantity: toOptionalNumber(raw.quantity),
    sellingMarkupFactor,
    costMarkupFactor,
    amount: toOptionalNumber(raw.amount),
    costAmount: toOptionalNumber(raw.costAmount),
    remark: (raw.remark as string | null | undefined) ?? null,
    version: Number(raw.version ?? 0),
    invoice: invoice as any,
    employee,
    expenseType: parseU3125ExpenseTypeRef(raw.expenseType),
    equipmentType: parseU3125EquipmentTypeRef(raw.equipmentType),
    task: parseU3125WipTaskRef(raw.task),
    rate: sellingRate ?? undefined,
    markupFactor: sellingMarkupFactor,
  };
}

/**
 * Extrait les lignes TEC depuis la réponse `GET /U3125/wipTransactions` ou `GET /U3125/:id`.
 * Pris en charge : facture directe avec `wipTransactions|wipLines|workInProgressLines|transactions`,
 * ou enveloppes `result` / `results` si le backend encapsule.
 */
export function parseWipTransactionsFromResult(payload: unknown): U3134WorkInProgressSearchRow[] {
  const mapArr = (arr: unknown[]) =>
    arr
      .filter((item) => item && typeof item === "object")
      .map((item) => normalizeU3125WipTransactionLine(item as Record<string, unknown>));

  if (Array.isArray(payload)) {
    return mapArr(payload);
  }
  if (payload && typeof payload === "object") {
    const o = payload as Record<string, unknown>;
    const fromResult = o.result;
    const nested =
      fromResult && typeof fromResult === "object" && !Array.isArray(fromResult)
        ? ((fromResult as Record<string, unknown>).wipTransactions ??
            (fromResult as Record<string, unknown>).workInProgressLines ??
            (fromResult as Record<string, unknown>).wipLines ??
            (fromResult as Record<string, unknown>).lines ??
            (fromResult as Record<string, unknown>).transactions)
        : undefined;
    const arr =
      (Array.isArray(o.results) ? o.results : undefined) ??
      (Array.isArray(fromResult) ? fromResult : undefined) ??
      nested ??
      o.wipTransactions ??
      o.workInProgressLines ??
      o.wipLines ??
      o.lines ??
      o.transactions;
    if (Array.isArray(arr)) {
      return mapArr(arr);
    }
  }
  return [];
}

export const doGetWipTransactions = (
  axios: AxiosInstance,
  params: { projectId: string; invoiceId?: string | null },
) => {
  const invoiceId = params.invoiceId?.trim();
  return axios.get(LogicalUnitEndpoints.WIP_TRANSACTIONS, {
    params: {
      projectId: params.projectId.trim(),
      ...(invoiceId ? { invoiceId } : {}),
      b: U2000GenerateUrlUUID(),
    },
  });
};

export const doCreateData = (axios: AxiosInstance, body: Record<string, unknown>) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: Record<string, unknown>) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doSetInvoiceStatus = (axios: AxiosInstance, id: string | number, body: U3125InvoiceSetStatusBody) => {
  return axios.post(LogicalUnitEndpoints.SET_STATUS(id), body);
};

export const doVoidInvoice = (axios: AxiosInstance, id: string | number, body: U3125InvoiceVoidBody) => {
  return axios.post<{ result: U3125Invoice }>(LogicalUnitEndpoints.VOID(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: { id: string; version: number }) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doCreatePayment = (axios: AxiosInstance, body: Record<string, unknown>) => {
  return axios.post<{ result: U3125Invoice }>(LogicalUnitEndpoints.PAYMENT_CREATE, body);
};

export const doUpdatePayment = (axios: AxiosInstance, id: string | number, body: Record<string, unknown>) => {
  return axios.post<{ result: U3125Invoice }>(LogicalUnitEndpoints.PAYMENT_UPDATE(id), body);
};

export const doDeletePayment = (axios: AxiosInstance, id: string | number, body: { id: string; version: number }) => {
  return axios.post<{ result: U3125Invoice }>(LogicalUnitEndpoints.PAYMENT_DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params: { b: U2000GenerateUrlUUID() } });
};

export function luHandleAppiError(_t: TranslateFn, NS: string, err: { errorCode?: string; description?: string }, errorResult: U2000ApiErrorResult): void {
  const code = err.errorCode;
  if (code === "B00007") {
    errorResult.genericErrors.push({
      title: _t("errorTitle", { ns: NS }),
      text: _t("errArAgingInvalidDate", { ns: NS, defaultValue: err.description ?? code }),
    });
    return;
  }
  if (code?.startsWith("BU3125")) {
    errorResult.genericErrors.push({
      title: _t("errorTitle", { ns: NS }),
      text: _t(`err${code}`, { ns: NS, defaultValue: err.description ?? code }),
    });
  }
}
